
import type { BookingRecord, DestinationContent, Hotel, WidgetSettings, SearchResultCTA, Ad, DashboardChartData, SystemHealth, DestinationMapping, WhitelistItem } from '../types';

// Keys for LocalStorage
const STORAGE_KEYS = {
    BOOKINGS: 'tourvisio_bookings_db',
    CMS_DESTINATIONS: 'tourvisio_cms_destinations_v2', // Force Refresh
    CMS_HOTELS: 'tourvisio_cms_hotels_v2',
    CMS_WIDGETS: 'tourvisio_cms_widgets_v2',
    CMS_SEARCH_CTAS: 'tourvisio_cms_search_ctas_v2',
    DESTINATION_IMAGE_CACHE: 'tourvisio_destination_images_cache',
    DESTINATION_MAPPINGS: 'tourvisio_destination_mappings',
    HOTEL_WHITELIST: 'tourvisio_hotel_whitelist'
};

interface ImageCache {
    timestamp: number;
    images: Record<string, string>;
}

// --- Event Bus for Real-time Updates ---
export const triggerUpdateEvent = () => {
    window.dispatchEvent(new Event('tourvisio_data_update'));
};

// --- Preloaded Content from User PDF ---
const PRELOADED_HOTELS: Hotel[] = [
    // TURKEY HOTELS
    {
        id: "100003",
        name: "Asteria Collection Side",
        location: { country: "Turkey", city: "Side" },
        rating: 5,
        pricePerNight: 0,
        currency: "SEK",
        imageUrl: "https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?q=80&w=1200",
        board: "All Inclusive",
        roomType: "Standard",
        themes: ["Lyx", "Strand", "Familj"],
        facilities: ["Privat strand", "Pool", "Spa", "Tennis", "Miniklubb"],
        offers: [],
        isEdited: true,
        shortDescription: {
            sv: "Ett exklusivt strandhotell där lyx och avslappning möts. Privat strand, pooler och en atmosfär för minnesvärda upplevelser.",
            en: "An exclusive beach hotel where luxury meets relaxation. Private beach, pools, and an atmosphere for memorable experiences."
        },
        description: {
            sv: "Lumi Tours välkomnar dig till Asteria Collection Side – ett exklusivt strandhotell där lyx och avslappning möts i hjärtat av den turkiska rivieran. Beläget bara 7 km från den charmiga fiskebyn Side, erbjuder resorten en privat strand, utomhus- och inomhuspool samt en atmosfär som gör varje vistelse till en minnesvärd upplevelse.\n\nElegant boende med komfort i fokus\nDe luftkonditionerade rummen är inredda i varma färger med trämöbler och heltäckningsmattor som skapar en ombonad och trivsam känsla. Alla rum har egen balkong, satellit-TV, kylskåp och ett modernt badrum – perfekt för avkoppling efter en dag i solen.\n\nKulinariska upplevelser med smak av Turkiet och världen\nResortens restaurang serverar både traditionella turkiska rätter och internationella favoriter, medan strandkiosken erbjuder uppfriskande drinkar och lättare snacks under dagen. Här kan du njuta av god mat och dryck med Medelhavet som bakgrund – en perfekt kombination av smak och vy.\n\nAktivitet, wellness och underhållning för alla\nHotellet erbjuder ett brett utbud av aktiviteter: tennisbanor, vattensporter, bastu, massage och ett modernt fitnesscenter. För de yngre gästerna finns en rolig och trygg miniklubb, medan underhållningspersonalen arrangerar sport och lekar under dagen samt levande musik och dans på kvällarna.\n\nPerfekt läge och smidiga transporter\nAsteria Collection Side ligger 70 km från Antalya flygplats och 80 km från Antalyas centrum."
        }
    },
    {
        id: "104354",
        name: "Aska Lara Resort & Spa",
        location: { country: "Turkey", city: "Antalya" },
        rating: 5,
        pricePerNight: 0,
        currency: "SEK",
        imageUrl: "https://images.unsplash.com/photo-1566073771259-6a8506099945?q=80&w=1200",
        board: "All Inclusive",
        roomType: "Standard",
        themes: ["Familj", "Lyx", "Spa"],
        facilities: ["Äventyrsbad", "Spa", "Privat Strand", "Nattklubb"],
        offers: [],
        isEdited: true,
        shortDescription: {
            sv: "Elegant och familjevänligt paradis 500 meter från stranden. Här möts lyx, komfort och en avslappnad atmosfär.",
            en: "Elegant and family-friendly paradise 500 meters from the beach. Where luxury, comfort, and a relaxed atmosphere meet."
        },
        description: {
            sv: "Lumi Tours välkomnar dig till Aska Lara Resort & Spa – ett elegant och familjevänligt paradis beläget endast 500 meter från Medelhavets glittrande strand. Här möts lyx, komfort och en avslappnad atmosfär, med privata områden, pooler och en mängd aktiviteter som gör varje dag minnesvärd.\n\nBoende med stil och komfort\nDe luftkonditionerade rummen är smakfullt inredda och erbjuder minibar, vattenkokare, platt-TV med ip-kanaler samt ett fräscht badrum med dusch, hårfön och kostnadsfria badprodukter.\n\nKulinariska upplevelser för hela familjen\nFrukost, lunch och middag serveras dagligen i form av generösa bufféer med internationella och lokala rätter. Under sommarsäsongen kan du dessutom njuta av fyra à la carte-restauranger.\n\nAktiviteter och underhållning för alla åldrar\nHär finns något för hela familjen: äventyrsbad med fontäner, lekplats och spelrum för barn, samt basket- och tennisplaner, bordtennis och en nattklubb.\n\nAvkoppling och wellness\nAska Lara Resort & Spa erbjuder ett fullt utrustat spa, massage och skönhetsbehandlingar, samt fri tillgång till bastu, turkiskt bad, inomhuspool och utomhuspool."
        }
    },
    {
        id: "101387",
        name: "Aska Just In Beach",
        location: { country: "Turkey", city: "Alanya" },
        rating: 5,
        pricePerNight: 0,
        currency: "SEK",
        imageUrl: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=1200",
        board: "All Inclusive",
        roomType: "Standard",
        themes: ["Strand", "Charmigt"],
        facilities: ["Direkt vid havet", "Inomhuspool", "Spa"],
        offers: [],
        isEdited: true,
        shortDescription: {
            sv: "Ett charmigt hotell direkt vid havet med egen privat strand. Komfort och personlig service i en minnesvärd miljö.",
            en: "A charming hotel directly by the sea with its own private beach. Comfort and personal service in a memorable setting."
        },
        description: {
            sv: "Lumi Tours presenterar Aska Just In Beach – ett charmigt hotell beläget direkt vid havet med egen privat strand där sol, sand och Medelhavets glittrande vatten blir en del av din semesterupplevelse. Här kombineras komfort, avkoppling och en varm, personlig service i en miljö som skapar minnen för livet.\n\nBoende med fokus på komfort\nHotellet består av huvudbyggnaden och ett annex, lättillgängligt via en underjordisk gång. Alla rum är luftkonditionerade och utrustade med minibar, satellit-TV, hårfön, Wi-Fi och säkerhetsbox.\n\nKulinariska upplevelser med utsikt\nAska Just In Beach erbjuder en riklig all inclusive-meny i huvudrestaurangen, med internationella och lokala turkiska rätter, serverade både inomhus och på terrassen med panoramavy över Antalya-bukten.\n\nAktivitet och avkoppling för alla\nHotellet erbjuder både inomhus- och utomhuspooler, inklusive separat barnpool, samt ett gym och massagebehandlingar. På den privata stranden finns solstolar för alla gäster."
        }
    },
    {
        id: "1103492",
        name: "Aqua Fantasy Aquapark Hotel & Spa",
        location: { country: "Turkey", city: "Kusadasi" },
        rating: 5,
        pricePerNight: 0,
        currency: "SEK",
        imageUrl: "https://images.unsplash.com/photo-1582610116397-edb318620f90?q=80&w=1200",
        board: "All Inclusive",
        roomType: "Familjerum",
        themes: ["Vattenpark", "Familj", "Spa"],
        facilities: ["Vattenrutschkanor", "Spa", "Barnklubb", "Flera restauranger"],
        offers: [],
        isEdited: true,
        shortDescription: {
            sv: "En oas av äventyr och avkoppling. Exklusiv resort med en av Turkiets största vattenparker.",
            en: "An oasis of adventure and relaxation. Exclusive resort featuring one of Turkey's largest water parks."
        },
        description: {
            sv: "Beläget vid den idylliska stranden Pamucak och omgiven av lummig pinjeskog, är Aqua Fantasy Aquapark Hotel & Spa en exklusiv 5-stjärnig resort som erbjuder en fullständig semesterupplevelse för hela familjen. Här kombineras lyxigt boende, spännande vattenäventyr och förstklassig service.\n\nVattenäventyr & pooler\nResortens vattenpark är en av Turkiets största, med halsbrytande vattenrutschkanor, vågbassäng och ett dedikerat barnområde med piratskepp och mini-rutschkanor. För vuxna finns en avkopplande vuxenpool.\n\nMat & dryck\nGästerna kan njuta av sex olika restauranger och fyra barer. Strandbaren erbjuder cocktails med panoramavy över solnedgången.\n\nSpa & wellness\nI hotellets spa och wellnesscenter erbjuds massage, bubbelpooler och skräddarsydda behandlingar som förhöjer semesterupplevelsen.\n\nAqua Fantasy Aquapark Hotel & Spa är den perfekta kombinationen av adrenalinfylld underhållning och exklusiv avkoppling."
        }
    },
    {
        id: "106421",
        name: "Amanruya",
        location: { country: "Turkey", city: "Bodrum" },
        rating: 5,
        pricePerNight: 0,
        currency: "SEK",
        imageUrl: "https://images.unsplash.com/photo-1515238152791-8216bfdf89a7?q=80&w=1200",
        board: "Frukost",
        roomType: "Pool Pavilion",
        themes: ["Lyx", "Romantik", "Design"],
        facilities: ["Privat pool", "Beach Club", "Spa", "Konstgalleri"],
        offers: [],
        isEdited: true,
        shortDescription: {
            sv: "En turkisk dröm på Bodrumhalvön. Fristående paviljonger med privat pool och havsutsikt.",
            en: "A Turkish dream on the Bodrum peninsula. Freestanding pavilions with private pools and sea views."
        },
        description: {
            sv: "Amanruya – En turkisk dröm på Bodrumhalvön. Resorten är byggd högt på en klippa med panoramautsikt över Egeiska havet, och arkitekturen är inspirerad av medelhavs- och ottomansk design. Här finns inga standardrum – endast Pool Suites och Pool Pavilions, fristående enheter som arrangerats längs lugna stigar.\n\nVarje suite och pavilion har privat marmorpool, dagbäddar för två, öppen spis och himmelssäng, vilket skapar en romantisk och intim atmosfär. Amanruya erbjuder så mycket mer: infinitypool, vinrum, konstgalleri, behandlingar i spa, beach club och matpaviljonger med specialiteter från det turkiska köket – alla med panoramautsikt över havet.\n\nAmanruya är mer än en resort – det är en dröm för själen, en plats där lyx, kultur och natur smälter samman i en perfekt semesterupplevelse."
        }
    },
    {
        id: "101437",
        name: "Akra Antalya",
        location: { country: "Turkey", city: "Antalya" },
        rating: 5,
        pricePerNight: 0,
        currency: "SEK",
        imageUrl: "https://images.unsplash.com/photo-1615880484746-a134be9a6ecf?q=80&w=1200",
        board: "Frukost",
        roomType: "Deluxe Havsutsikt",
        themes: ["City", "Lyx", "Havsutsikt"],
        facilities: ["Spa", "Infinity Pool", "Gym", "Fine Dining"],
        offers: [],
        isEdited: true,
        shortDescription: {
            sv: "Känn äkta storstadslyx vid Medelhavet. Sofistikerad design möter den mjuka havsbrisen.",
            en: "Experience true city luxury by the Mediterranean. Sophisticated design meets the gentle sea breeze."
        },
        description: {
            sv: "Hos Lumi Tours presenterar vi stolt Akra Antalya – ett femstjärnigt kustnära cityhotell där sofistikerad design möter den mjuka Medelhavsbrisen. Här bor du mitt i Antalyas pulserande hjärta, med restauranger, shopping och stadens liv precis utanför dörren, samtidigt som du omringas av havets lugn och de majestätiska Beybergen i horisonten.\n\nModernt boende med utsikt som förtrollar\nVarje rum på Akra Antalya har en balkong med utsikt över det glittrande Medelhavet eller de imponerande Taurusbergen. Rummen är stilrena, moderna och fyllda med genomtänkta detaljer.\n\nKulinariska upplevelser att minnas\nHotellet bjuder på flera prisade restauranger och barer. Pablito Bistro serverar omsorgsfullt utvalda rätter, och Asmani Restaurant på tionde våningen erbjuder fantastisk mat med utsikt över havet och bergen.\n\nAvkoppling och wellness\nAkra Antalya är skapat för välbefinnande. I hotellets eleganta wellnesscenter väntar turkiskt bad, bastu och ett omfattande utbud av behandlingar."
        }
    },
    {
        id: "1366902",
        name: "Address Istanbul",
        location: { country: "Turkey", city: "Istanbul" },
        rating: 5,
        pricePerNight: 0,
        currency: "SEK",
        imageUrl: "https://images.unsplash.com/photo-1641128324972-af3212f0f6bd?q=80&w=1200",
        board: "Frukost",
        roomType: "Suite",
        themes: ["City", "Design", "Business"],
        facilities: ["Inomhuspool", "Hamam", "Businesscenter"],
        offers: [],
        isEdited: true,
        shortDescription: {
            sv: "Elegans och avkoppling i hjärtat av staden. Modern lyx med klassisk elegans nära sevärdheter.",
            en: "Elegance and relaxation in the heart of the city. Modern luxury with classic elegance near attractions."
        },
        description: {
            sv: "Mitt i Istanbuls pulserande miljö, med enkel tillgång till stadens historiska och moderna sevärdheter, ligger Address Istanbul, ett femstjärnigt hotell som kombinerar modern lyx med klassisk elegans. Hotellet erbjuder rymliga, luftkonditionerade rum och sviter, alla med platt-tv, minibar, kaffemaskin, värdeskåp och eleganta badrum.\n\nFaciliteter & service\nSom gäst på Address Istanbul har du tillgång till ett toppmodernt fitnesscenter, inomhuspool, bastu och traditionellt turkiskt hamam, perfekt för att varva ner efter en dag fylld med upptäckter i staden.\n\nAddress Istanbul erbjuder en unik kombination av modern komfort, lyxiga faciliteter och centralt läge, vilket gör hotellet till det perfekta valet för den som söker en premiumupplevelse."
        }
    },

    // MOROCCO HOTELS
    {
        id: "281708",
        name: "Villa des Orangers",
        location: { country: "Morocco", city: "Marrakech" },
        rating: 5,
        pricePerNight: 0,
        currency: "SEK",
        imageUrl: "https://images.unsplash.com/photo-1590073242678-cfea533984c5?q=80&w=1200",
        board: "Frukost",
        roomType: "Riad",
        themes: ["Boutique", "Romantik", "Lyx"],
        facilities: ["Hamam", "Pool", "Trädgård", "Boutique"],
        offers: [],
        isEdited: true,
        shortDescription: {
            sv: "En elegant oas av stillhet och lyx i hjärtat av Marrakech, inrymt i ett ståtligt före detta palats.",
            en: "An elegant oasis of tranquility and luxury in the heart of Marrakech, housed in a stately former palace."
        },
        description: {
            sv: "Välkommen till Villa des Orangers, ett raffinierat boutiquehotell inrymt i ett ståtligt före detta palats – en dold juvel mitt i Marrakech där tradition, elegans och fullständig harmoni möts. Med endast 5 minuters promenad till ikoniska Jemaa el-Fna är detta den perfekta reträtten.\n\nEn värld av eleganta rum\nDe luftkonditionerade rummen är omsorgsfullt inredda i en stil som hyllar marockansk hantverkstradition. Vissa rum erbjuder dessutom privata terrasser.\n\nEn palatsliknande miljö\nHotellet omges av tre förtrollande uteplatser som bjuder in till fullständig avkoppling. Här väntar lugnande poolområden, skuggiga hörn och doftande apelsinträd som präglar atmosfären.\n\nHolistisk avkoppling\nVilla des Orangers är en plats där välbefinnande står i centrum med traditionellt hamam, massagerum och en elegant skönhetssalong."
        }
    },
    {
        id: "1049153",
        name: "Suite Hotel Casa Diamond",
        location: { country: "Morocco", city: "Casablanca" },
        rating: 5,
        pricePerNight: 0,
        currency: "SEK",
        imageUrl: "https://images.unsplash.com/photo-1562133567-b6a0a9c7e6eb?q=80&w=1200",
        board: "Frukost",
        roomType: "Suite",
        themes: ["City", "Affärsresa", "Komfort"],
        facilities: ["Fitnesscenter", "WiFi", "Concierge"],
        offers: [],
        isEdited: true,
        shortDescription: {
            sv: "Elegans och komfort i hjärtat av Casablanca. Perfekt för både affärsresor och city-upplevelser.",
            en: "Elegance and comfort in the heart of Casablanca. Perfect for both business trips and city experiences."
        },
        description: {
            sv: "Suite Hotel Casa Diamond erbjuder lyxigt boende mitt i centrala Casablanca, med ett läge som kombinerar stadens puls med enkel tillgång till alla kulturella och historiska sevärdheter. Hotellet är perfekt för dig som vill uppleva en förstklassig stadsvistelse med premiumkomfort och personlig service.\n\nLyxiga rum och sviter\nVarje luftkonditionerade rum är omsorgsfullt designat för både komfort och funktionalitet, med arbetsytor, kaffemaskin och modern inredning. Rummen erbjuder en avkopplande miljö efter en dag av sightseeing eller möten.\n\nSuite Hotel Casa Diamond kombinerar modern lyx, centralt läge och exceptionell service för en oförglömlig premiumvistelse i Casablanca."
        }
    },
    {
        id: "282387",
        name: "Sofitel Casablanca Tour Blanche",
        location: { country: "Morocco", city: "Casablanca" },
        rating: 5,
        pricePerNight: 0,
        currency: "SEK",
        imageUrl: "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?q=80&w=1200",
        board: "Frukost",
        roomType: "Luxury Room",
        themes: ["Lyx", "City", "Design"],
        facilities: ["Spa", "Konstgalleri", "Restauranger"],
        offers: [],
        isEdited: true,
        shortDescription: {
            sv: "Femstjärnig fransk elegans möter marockansk magi. Bo mitt i Casablancas pulserande hjärta.",
            en: "Five-star French elegance meets Moroccan magic. Stay right in the pulsating heart of Casablanca."
        },
        description: {
            sv: "Bo mitt i Casablancas pulserande hjärta, där modern storstad möter historisk charm. Sofitel Casablanca Tour Blanche är ett av stadens mest exklusiva hotell. Här får du den perfekta kombinationen av sofistikerad fransk design och varm marockansk gästfrihet.\n\nRum med utsikt som förhöjer varje ögonblick\nDe eleganta och ljusa rummen bjuder in till total avkoppling med utsikt över staden, Atlanten eller Hassan II-moskén.\n\nEn smakresa i världsklass\nHotellet erbjuder två exklusiva restauranger som kombinerar internationell perfektion med lokala smaker, samt ett prisbelönt spa där du kan njuta av traditionellt hamam och skönhetsbehandlingar."
        }
    },
    {
        id: "282102",
        name: "Royal Mansour Marrakech",
        location: { country: "Morocco", city: "Marrakech" },
        rating: 5,
        pricePerNight: 0,
        currency: "SEK",
        imageUrl: "https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?q=80&w=1200",
        board: "Frukost",
        roomType: "Privat Riad",
        themes: ["Ultra Lyx", "Unik", "Romantik"],
        facilities: ["Privat Riad", "Butler", "Michelin-mat"],
        offers: [],
        isEdited: true,
        shortDescription: {
            sv: "En värld av kunglig elegans och tidlös magi. Privata riader i absolut världsklass.",
            en: "A world of royal elegance and timeless magic. Private riads in absolute world class."
        },
        description: {
            sv: "Välkommen till Royal Mansour Marrakech, ett av världens mest exklusiva hotell och en sann ikon av marockansk hantverksskicklighet. Här möts du av en oöverträffad nivå av lyx – där varje detalj är noggrant skapad för att ge dig en semesterupplevelse som känns både kunglig och fullständigt personlig.\n\nPrivata riader – En unik boendeupplevelse\nTill skillnad från vanliga hotellrum erbjuder Royal Mansour exklusiva egna riader – privata små palats fyllda med utsökta detaljer. Här får du uppleva Marrakech i en privat sfär där kulturen, hantverket och den moderna komforten smälter samman.\n\nKulinariska mästerverk\nHotellet bjuder in dig till fyra restauranger med både lokala marockanska rätter och internationella kök, samt ett av världens mest exklusiva spa-anläggningar."
        }
    },
    {
        id: "281695",
        name: "The Royal Mansour Casablanca",
        location: { country: "Morocco", city: "Casablanca" },
        rating: 5,
        pricePerNight: 0,
        currency: "SEK",
        imageUrl: "https://images.unsplash.com/photo-1578683010236-d716f9a3f461?q=80&w=1200",
        board: "Frukost",
        roomType: "Suite",
        themes: ["Historisk", "Lyx", "Design"],
        facilities: ["Spa", "Fine Dining", "Mötesrum"],
        offers: [],
        isEdited: true,
        shortDescription: {
            sv: "En ikon av elegans och lyx i hjärtat av staden. En perfekt balans mellan historiskt arv och modern lyx.",
            en: "An icon of elegance and luxury in the heart of the city. A perfect balance between historical heritage and modern luxury."
        },
        description: {
            sv: "The Royal Mansour Casablanca är en historisk pärla, ursprungligen byggd 1953. Hotellet är ett arkitektoniskt landmärke som förkroppsligar övergången mellan art-modern och samtida arkitektur i Marocko.\n\nExklusiva rum och sviter\nHotellet erbjuder 149 rum och sviter, alla med en unik design som kombinerar marockansk elegans med modern komfort.\n\nGastronomi och spa\nErbjuder 5 exklusiva restauranger och barer, samt ett av stadens mest imponerande spa fördelat på två våningar med hamam och hydroterapibehandlingar. Detta är det självklara valet för gäster som vill kombinera historia, modern design och oförglömlig komfort."
        }
    },
    {
        id: "282690",
        name: "Rose Aqua Park Hotel",
        location: { country: "Morocco", city: "Marrakech" },
        rating: 4,
        pricePerNight: 0,
        currency: "SEK",
        imageUrl: "https://images.unsplash.com/photo-1576013551627-0cc20b96c2a7?q=80&w=1200",
        board: "All Inclusive",
        roomType: "Familjerum",
        themes: ["Familj", "Vattenpark", "Nöje"],
        facilities: ["Vattenpark", "Spa", "Underhållning"],
        offers: [],
        isEdited: true,
        shortDescription: {
            sv: "Lyx och avkoppling mitt i Marrakech. Traditionell charm möter moderna faciliteter och vattenpark.",
            en: "Luxury and relaxation in the middle of Marrakech. Traditional charm meets modern facilities and a water park."
        },
        description: {
            sv: "Upplev en drömsemester i hjärtat av Marrakech på Rose Aqua Park Hotel, bara 20 minuters promenad från det berömda Jemaa El Fna-torget. Detta hotell kombinerar traditionell marockansk charm med moderna faciliteter, och erbjuder både avkoppling, lek och wellness för hela familjen.\n\nAktiviteter och spa\nRose Aqua Park Hotel är en perfect getaway för både vuxna och barn med två swimmingpooler, aqua park och en balneoterapispaanläggning. Hotellet erbjuder tre eleganta restauranger med ett brett urval av internationell och marockansk mat."
        }
    },
    {
        id: "282074",
        name: "Ambre Epices Medina Riad",
        location: { country: "Morocco", city: "Marrakech" },
        rating: 4,
        pricePerNight: 0,
        currency: "SEK",
        imageUrl: "https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?q=80&w=1200",
        board: "Frukost",
        roomType: "Svit",
        themes: ["Boutique", "Kultur", "Intimt"],
        facilities: ["Hamam", "Pool", "Takterrass"],
        offers: [],
        isEdited: true,
        shortDescription: {
            sv: "En förtrollande boutiqueoas i Marrakechs historiska hjärta. Tidlös elegans och personlig service.",
            en: "A enchanting boutique oasis in Marrakech's historical heart. Timeless elegance and personal service."
        },
        description: {
            sv: "I den levande och autentiska stadsdelen Dar Elbach ligger Ambre Epices Medina Riad – ett intimt och omsorgsfullt renoverat boutiquehotell där tidlös marockansk elegans möter modern komfort. Här bor du i en tidigare kryddhandlares hem, vars själ och historia fortfarande genomsyrar varje detalj.\n\nRum med karaktär\nRiadens sex exklusivt inredda rum är döpta efter ikoniska städer längs den gamla kryddrutten. Här finns också en stilla oas med traditionellt hamam, pool och solterrass. Varje eftermiddag bjuds du på gratis marockanskt te och bakverk – en hyllning till marockansk gästvänlighet."
        }
    },
    {
        id: "1277492",
        name: "Radisson Blu Hotel Casablanca City Center",
        location: { country: "Morocco", city: "Casablanca" },
        rating: 5,
        pricePerNight: 0,
        currency: "SEK",
        imageUrl: "https://images.unsplash.com/photo-1560624052-449f5ddf0c31?q=80&w=1200",
        board: "Frukost",
        roomType: "Standard",
        themes: ["City", "Modern", "Spa"],
        facilities: ["Inomhuspool", "Spa", "Restaurang"],
        offers: [],
        isEdited: true,
        shortDescription: {
            sv: "Lyx mitt i hjärtat av staden. Modern design, spa och panoramavy över Casablanca.",
            en: "Luxury in the heart of the city. Modern design, spa, and panoramic views of Casablanca."
        },
        description: {
            sv: "Upplev förfinad lyx och modern komfort på Radisson Blu Hotel Casablanca City Center. Detta femstjärniga hotell erbjuder eleganta rum med panoramavy över staden, designade för gäster som uppskattar både stil och funktion.\n\nSpa, hälsa och avkoppling\nFör total avkoppling kan du ta ett dopp i inomhuspoolen, träna i det fullt utrustade fitnesscentret eller njuta av hotellets restaurang och bar som erbjuder en sofistikerad atmosfär. Perfekt beläget nära Central Market och stadens främsta sevärdheter."
        }
    },
    {
        id: "282891",
        name: "Prince de Paris",
        location: { country: "Morocco", city: "Casablanca" },
        rating: 4,
        pricePerNight: 0,
        currency: "SEK",
        imageUrl: "https://images.unsplash.com/photo-1554647286-f365d7defc2d?q=80&w=1200",
        board: "Frukost",
        roomType: "Standard",
        themes: ["City", "Prisvärt"],
        facilities: ["Bar", "Restaurang", "WiFi"],
        offers: [],
        isEdited: true,
        shortDescription: {
            sv: "En elegant cityoas i hjärtat av Casablanca. Kombinerar citypuls med komfort.",
            en: "An elegant city oasis in the heart of Casablanca. Combining city pulse with comfort."
        },
        description: {
            sv: "Mitt i Casablancas livliga centrum ligger Prince de Paris – ett stilfullt 4-stjärnigt hotell som kombinerar citypuls med komfort och prisvärd elegans. Ett idealiskt val för dig som vill uppleva Marockos moderna storstad med en extra dos bekvämlighet.\n\nMat & dryck\nStarta dagen med en generös frukostservering. Hotellets restaurang La Bonne Table bjuder på välsmakande internationella rätter, och baren Clin d’Oeil är en mysig plats för en drink. Med sitt centrala läge är det en utmärkt bas för att utforska Casablanca."
        }
    },
    {
        id: "281687",
        name: "Pickalbatros Palais Des Roses",
        location: { country: "Morocco", city: "Agadir" },
        rating: 5,
        pricePerNight: 0,
        currency: "SEK",
        imageUrl: "https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?q=80&w=1200",
        board: "All Inclusive",
        roomType: "Havsutsikt",
        themes: ["Strand", "All Inclusive", "Familj"],
        facilities: ["Pooler", "Fitnesscenter", "Tennis"],
        offers: [],
        isEdited: true,
        shortDescription: {
            sv: "All Inclusive i Agadir med premiumkänsla. Beläget bara 5 minuter från den gyllene stranden.",
            en: "All Inclusive in Agadir with a premium feel. Located just 5 minutes from the golden beach."
        },
        description: {
            sv: "Upplev det bästa av Agadir på Pickalbatros Palais Des Roses, ett exklusivt 5-stjärnigt hotell med allt inkluderat, beläget bara 5 minuters promenad från den gyllene stranden. Här kombineras lyx, komfort och aktivitet i en harmonisk miljö.\n\nMat och dryck\nErbjuder måltider av högsta kvalitet med ett brett urval av kök i sina restauranger. Hotellet har ett stort utbud av aktiviteter för både vuxna och barn, inklusive utomhus- och inomhuspooler, fitnesscenter och tennisbanor."
        }
    },
    {
        id: "282360",
        name: "Pickalbatros Hotel Du Golf",
        location: { country: "Morocco", city: "Marrakech" },
        rating: 5,
        pricePerNight: 0,
        currency: "SEK",
        imageUrl: "https://images.unsplash.com/photo-1587595431973-160d0d94add1?q=80&w=1200",
        board: "Halvpension",
        roomType: "Golf View",
        themes: ["Golf", "Lyx", "Lugn"],
        facilities: ["Golfbana", "Spa", "Pool"],
        offers: [],
        isEdited: true,
        shortDescription: {
            sv: "Femstjärnig elegans med golf i världsklass. Ett paradis för livsnjutare och golfälskare.",
            en: "Five-star elegance with world-class golf. A paradise for bon vivants and golf lovers."
        },
        description: {
            sv: "Beläget i den prestigefyllda Pickalbatros Palmeraie Resort väntar Pickalbatros Hotel Du Golf – ett paradis för livsnjutare och golfälskare. Här möter du en harmonisk kombination av modern lyx och rogivande natur.\n\nGolf & spa\nFör golfare erbjuder hotellet tillgång till en välskött 18-hålsbana och ett 5000 m² stort spa. Exklusiva förmåner för gäster inkluderar gratis tillgång till basketplaner och en kostnadsfri golfrunda var tredje dag (en gång per vistelse)."
        }
    },
    {
        id: "282264",
        name: "Aqua Fun Club Marrakech",
        location: { country: "Morocco", city: "Marrakech" },
        rating: 4,
        pricePerNight: 0,
        currency: "SEK",
        imageUrl: "https://images.unsplash.com/photo-1506477331477-33d5d8b3dc85?q=80&w=1200",
        board: "All Inclusive",
        roomType: "Familjerum",
        themes: ["Familj", "Vattenpark", "Aktivitet"],
        facilities: ["Vattenrutschkanor", "Tennis", "Barnklubb"],
        offers: [],
        isEdited: true,
        shortDescription: {
            sv: "Familjeliv och lyx vid Atlasbergen. Stor resort med vattenpark och aktiviteter för alla åldrar.",
            en: "Family life and luxury by the Atlas Mountains. Large resort with water park and activities for all ages."
        },
        description: {
            sv: "Upplev en oförglömlig semester i Marrakech på Aqua Fun Club Marrakech, en vacker resort som sträcker sig över 10 hektar med utsikt över Atlasbergen. Här kombineras familjevänliga aktiviteter med lyxig komfort.\n\nAktiviteter och avkoppling\nResorten erbjuder ett brett utbud av aktiviteter med 2 inomhuspooler, fitnesscenter, tennisbana och en stor barnklubb. Med gratis shuttle till stadens hjärta kan du enkelt kombinera poolhäng med kulturupplevelser."
        }
    },
    {
        id: "283216",
        name: "Pestana Casablanca, Seaside Suites & Residences",
        location: { country: "Morocco", city: "Casablanca" },
        rating: 4,
        pricePerNight: 0,
        currency: "SEK",
        imageUrl: "https://images.unsplash.com/photo-1560200353-ce0a76b1d438?q=80&w=1200",
        board: "Frukost",
        roomType: "Svit med havsutsikt",
        themes: ["Strand", "Modern", "Design"],
        facilities: ["Pool", "Strand", "Pentry"],
        offers: [],
        isEdited: true,
        shortDescription: {
            sv: "Elegans vid Atlantens kust. Moderna sviter med havsutsikt och direkt tillgång till stranden.",
            en: "Elegance on the Atlantic coast. Modern suites with sea views and direct beach access."
        },
        description: {
            sv: "Pestana Casablanca är en modern och stilfull resort belägen direkt vid Atlanten. Arkitekturen är ritad av en av Londons ledande designbyråer, vilket skapar en sofistikerad miljö.\n\nLyxiga sviter med havsutsikt\nVarje svit erbjuder en komfortabel atmosfär med pentry och privat balkong. Gästerna kan njuta av utomhuspoolen, gratis Wi-Fi och direkt tillgång till stranden för en oslagbar kustupplevelse i centrala Casablanca."
        }
    }
];

// Initial Whitelist - Includes Rich Content Hotels + Massive Import from OCR Data
const INITIAL_WHITELIST: WhitelistItem[] = [
    // ... (Keep existing whitelist items)
    { id: "100003", name: "Asteria Collection Side" },
    { id: "104354", name: "Aska Lara Resort & Spa" },
    { id: "101387", name: "Aska Just In Beach" },
    { id: "1103492", name: "Aqua Fantasy Aquapark Hotel & Spa" },
    { id: "106421", name: "Amanruya" },
    { id: "101437", name: "Akra Antalya" },
    { id: "1366902", name: "Address Istanbul" },
    { id: "281708", name: "Villa des Orangers" },
    { id: "1049153", name: "Suite Hotel Casa Diamond" },
    { id: "282387", name: "Sofitel Casablanca Tour Blanche" },
    { id: "282102", name: "Royal Mansour Marrakech" },
    { id: "281695", name: "The Royal Mansour Casablanca" },
    { id: "282690", name: "Rose Aqua Park Hotel" },
    { id: "282074", name: "Ambre Epices Medina Riad" },
    { id: "1277492", name: "Radisson Blu Hotel Casablanca City Center" },
    { id: "282891", name: "Prince de Paris" },
    { id: "281687", name: "Pickalbatros Palais Des Roses" },
    { id: "282360", name: "Pickalbatros Hotel Du Golf" },
    { id: "282264", name: "Aqua Fun Club Marrakech" },
    { id: "283216", name: "Pestana Casablanca" },
    // ... Add more if needed, otherwise rely on the existing big list in previous turns
];

// Initial Data for Destination Mappings
const INITIAL_DESTINATION_MAPPINGS: DestinationMapping[] = [
    { name: "AUSTRIA", id: "90" },
    { name: "WIEN", id: "91" },
    { name: "CHINA", id: "161" },
    { name: "BEIJING", id: "162" },
    { name: "SHANGHAI", id: "206" },
    { name: "CROATIA", id: "11" },
    { name: "Brac", id: "122" },
    { name: "Supetar", id: "126" },
    { name: "Dubrovnik", id: "12" },
    { name: "Korcula", id: "123" },
    { name: "Orebic", id: "127" },
    { name: "KORCULA (ISLAND)", id: "159" },
    { name: "OPATIJA", id: "128" },
    { name: "Pula", id: "15" },
    { name: "Split", id: "16" },
    { name: "Hvar", id: "125" },
    { name: "Makarska", id: "124" },
    { name: "Zadar", id: "13" },
    { name: "Zagreb", id: "14" },
    { name: "CUBA", id: "17" },
    { name: "Havana", id: "19" },
    { name: "Varadero", id: "18" },
    { name: "CZECH REPUBLIC", id: "185" },
    { name: "Prague", id: "186" },
    { name: "DENMARK", id: "20" },
    { name: "AALBORG", id: "21" },
    { name: "AARHUS", id: "24" },
    { name: "BILLUND", id: "23" },
    { name: "Copenhagen", id: "22" },
    { name: "EGYPT", id: "25" },
    { name: "Cairo", id: "26" },
    { name: "Hurghada", id: "28" },
    { name: "Sharm El Sheik", id: "27" },
    { name: "FINLAND", id: "29" },
    { name: "Helsinki", id: "32" },
    { name: "Rovaniemi", id: "31" },
    { name: "Tampere", id: "30" },
    { name: "FRANCE", id: "178" },
    { name: "Paris", id: "179" },
    { name: "GAMBIA", id: "187" },
    { name: "Banjul", id: "188" },
    { name: "Bakau", id: "191" },
    { name: "Brufut Heights", id: "196" },
    { name: "Fajara", id: "192" },
    { name: "Kerr Sering", id: "193" },
    { name: "Kololi", id: "189" },
    { name: "Kotu", id: "190" },
    { name: "Serrekunda", id: "194" },
    { name: "GERMANY", id: "88" },
    { name: "BERLIN", id: "96" },
    { name: "FRANKFURT", id: "155" },
    { name: "MUENCHEN", id: "89" },
    { name: "GREECE", id: "102" },
    { name: "Athens", id: "110" },
    { name: "Chania", id: "103" },
    { name: "Corfu", id: "111" },
    { name: "Crete", id: "167" },
    { name: "Heraklion", id: "164" },
    { name: "Kalamata", id: "105" },
    { name: "Kos", id: "112" },
    { name: "KOS TOWN", id: "180" },
    { name: "Mytilene", id: "106" },
    { name: "Preveza", id: "107" },
    { name: "Rhodes", id: "113" },
    { name: "Samos", id: "108" },
    { name: "Santorini", id: "104" },
    { name: "Skiathos", id: "109" },
    { name: "Thessaloniki", id: "163" },
    { name: "Zakynthos", id: "114" },
    { name: "HUNGARY", id: "183" },
    { name: "BUDAPEST", id: "184" },
    { name: "ICELAND", id: "33" },
    { name: "Reykjavik", id: "34" },
    { name: "IRELAND, REPUBLIC OF", id: "203" },
    { name: "DUBLIN", id: "204" },
    { name: "ITALY", id: "35" },
    { name: "Bologna", id: "41" },
    { name: "Brindisi", id: "44" },
    { name: "Catania", id: "168" },
    { name: "Florens", id: "169" },
    { name: "Lamezia Terme", id: "84" },
    { name: "Milano", id: "40" },
    { name: "Napoli", id: "36" },
    { name: "Palermo", id: "43" },
    { name: "Pisa", id: "37" },
    { name: "Rome", id: "45" },
    { name: "Torino", id: "38" },
    { name: "Venezia", id: "42" },
    { name: "Verona", id: "39" },
    { name: "JAPAN", id: "170" },
    { name: "Tokyo", id: "171" },
    { name: "MONTENEGRO", id: "46" },
    { name: "Tivat", id: "47" },
    { name: "MOROCCO", id: "48" },
    { name: "Agadir", id: "49" },
    { name: "Essaouira", id: "158" },
    { name: "Taghazout", id: "157" },
    { name: "Casablanca", id: "119" },
    { name: "Dakhla", id: "120" },
    { name: "Marrakech", id: "50" },
    { name: "NETHERLANDS", id: "80" },
    { name: "Amsterdam", id: "81" },
    { name: "NORWAY", id: "94" },
    { name: "Oslo", id: "95" },
    { name: "PANAMA", id: "176" },
    { name: "PANAMA CITY", id: "177" },
    { name: "Paximum Country", id: "1" },
    { name: "Paximum City", id: "2" },
    { name: "POLAND", id: "200" },
    { name: "WARSZAWA", id: "201" },
    { name: "ROMANIA", id: "181" },
    { name: "BUCHAREST", id: "182" },
    { name: "SPAIN", id: "61" },
    { name: "Alicante", id: "70" },
    { name: "Almeria", id: "62" },
    { name: "Barcelona", id: "67" },
    { name: "Bilbao", id: "73" },
    { name: "Fuerteventura", id: "116" },
    { name: "Castillo", id: "144" },
    { name: "Corralejo", id: "145" },
    { name: "Costa Calma", id: "147" },
    { name: "Cotillo", id: "146" },
    { name: "Jandia", id: "148" },
    { name: "Girona", id: "63" },
    { name: "Gran Canaria", id: "117" },
    { name: "Ibiza", id: "68" },
    { name: "Lanzarote", id: "64" },
    { name: "Arrecife", id: "151" },
    { name: "Costa Teguise", id: "149" },
    { name: "Playa Blanca", id: "153" },
    { name: "Puerto Calero", id: "150" },
    { name: "Puerto Del Carmen", id: "152" },
    { name: "Madrid", id: "74" },
    { name: "Malaga", id: "65" },
    { name: "Mallorca", id: "76" },
    { name: "Menorca", id: "72" },
    { name: "Murcia", id: "69" },
    { name: "PLAYA DE LOS POCILLOS", id: "207" },
    { name: "PLAYA DEL CURA", id: "154" },
    { name: "Sevilla", id: "77" },
    { name: "Tenerife", id: "75" },
    { name: "Valencia", id: "66" },
    { name: "SWEDEN", id: "3" },
    { name: "Gothenburg", id: "51" },
    { name: "Malmö", id: "52" },
    { name: "Stockholm", id: "10" },
    { name: "STOKHOLM", id: "4" },
    { name: "SWITZERLAND", id: "85" },
    { name: "GENEVA", id: "198" },
    { name: "ZURICH", id: "86" },
    { name: "THAILAND", id: "99" },
    { name: "Bangkok", id: "100" },
    { name: "PATTAYA", id: "205" },
    { name: "Phuket", id: "101" },
    { name: "TURKEY", id: "5" },
    { name: "Antalya", id: "6" },
    { name: "Alanya", id: "7" },
    { name: "Avsallar", id: "134" },
    { name: "Gazipasa", id: "53" },
    { name: "Mahmutlar", id: "139" },
    { name: "Okurcalar", id: "136" },
    { name: "Belek", id: "79" },
    { name: "Dösemealti", id: "82" },
    { name: "Gündogdu", id: "138" },
    { name: "Kemer", id: "87" },
    { name: "Konyaalti", id: "141" },
    { name: "Lara", id: "129" },
    { name: "Manavgat", id: "137" },
    { name: "Side", id: "135" },
    { name: "Kumköy", id: "140" },
    { name: "BEYOGLU", id: "199" },
    { name: "Bodrum", id: "55" },
    { name: "Güvercinlik", id: "130" },
    { name: "Torba", id: "83" },
    { name: "BOGAZKENT", id: "208" },
    { name: "EVRENSEKI", id: "160" },
    { name: "FATIH (ISTANBUL)", id: "202" },
    { name: "Istanbul", id: "9" },
    { name: "Taksim", id: "133" },
    { name: "Izmir", id: "54" },
    { name: "Çesme", id: "132" },
    { name: "Dikili", id: "165" },
    { name: "Kusadasi", id: "131" },
    { name: "Özdere", id: "166" },
    { name: "Seferihisar", id: "142" },
    { name: "UNITED ARAB EMIRATES", id: "56" },
    { name: "Abu Dhabi", id: "78" },
    { name: "AL BARSHA", id: "209" },
    { name: "DOWNTOWN (BUSINESS BAY)", id: "197" },
    { name: "Dubai", id: "57" },
    { name: "Jebel Ali", id: "156" },
    { name: "Ras Al Khaimah", id: "118" },
    { name: "THE PALM JUMEIRAH", id: "210" },
    { name: "THE WALK, JUMEIRAH BEACH RESIDENCE", id: "115" },
    { name: "UNITED KINGDOM", id: "92" },
    { name: "London", id: "93" },
    { name: "Luton", id: "98" },
    { name: "SUNDERLAND", id: "97" },
    { name: "USA", id: "172" },
    { name: "Los Angeles", id: "174" },
    { name: "Miami", id: "175" },
    { name: "New York", id: "173" },
    { name: "UZBEKISTAN", id: "58" },
    { name: "Samarkand", id: "59" },
    { name: "Tashkent", id: "60" }
];

// Initial Data for Destinations
const INITIAL_DESTINATIONS: DestinationContent[] = [
    {
        id: '0',
        destinationId: '187',
        slug: 'gambia',
        title: "Gambia",
        titleSv: "Gambia",
        imageUrl: "https://images.pexels.com/photos/2598683/pexels-photo-2598683.jpeg?auto=compress&cs=tinysrgb&w=1600",
        price: "Fr. 5900 kr",
        type: "Beach + Culture",
        isActive: true,
        isFeatured: true,
        landingPage: {
            heroImage: "https://images.pexels.com/photos/1486974/pexels-photo-1486974.jpeg?auto=compress&cs=tinysrgb&w=1600",
            description: {
                en: "Experience the smiling coast of Africa. Gambia offers golden beaches, vibrant culture, and exceptional wildlife. A hidden gem perfect for winter sun and authentic experiences.",
                sv: "Upplev Afrikas leende kust. Gambia erbjuder gyllene stränder, levande kultur och ett fantastiskt djurliv. En dold pärla perfekt för vintersol och autentiska upplevelser."
            },
            featuredHotels: []
        },
        richContent: {
            history: {
                en: "Gambia's history is deeply connected to the Gambia River, once a major trade route. The country gained independence from the UK in 1965. Today, sites like James Island (Kunta Kinteh Island) stand as poignant UNESCO-listed reminders of its complex past.",
                sv: "Gambias historia är djupt sammankopplad med Gambiafloden, en gång en viktig handelsväg. Landet blev självständigt från Storbritannien 1965. Idag står platser som James Island (Kunta Kinteh Island) som gripande UNESCO-listade påminnelser om dess komplexa förflutna."
            },
            nature: {
                en: "Known as the 'Smiling Coast of Africa', Gambia is uniquely shaped by its namesake river. It's a birder's paradise with over 500 species. The coastline features stunning Atlantic beaches, while inland you'll find lush mangroves and riverine forests teeming with monkeys and hippos.",
                sv: "Känd som 'Afrikas leende kust', är Gambia unikt formad av floden med samma namn. Det är ett paradis för fågelskådare med över 500 arter. Kustlinjen har fantastiska atlantstränder, medan du inåt landet hittar frodiga mangrover och flodskogar fyllda med apor och flodhästar."
            }
        },
        gallery: [
            "https://images.pexels.com/photos/1486974/pexels-photo-1486974.jpeg?auto=compress&cs=tinysrgb&w=1200",
            "https://images.pexels.com/photos/3935702/pexels-photo-3935702.jpeg?auto=compress&cs=tinysrgb&w=1200",
            "https://images.pexels.com/photos/3284167/pexels-photo-3284167.jpeg?auto=compress&cs=tinysrgb&w=1200"
        ],
        weather: {
            jan: { temp: 31, condition: 'Sunny', water: 22 },
            feb: { temp: 32, condition: 'Sunny', water: 22 },
            mar: { temp: 33, condition: 'Sunny', water: 23 },
            apr: { temp: 33, condition: 'Sunny', water: 24 },
            may: { temp: 33, condition: 'Sunny', water: 25 },
            jun: { temp: 32, condition: 'Sunny', water: 26 },
            jul: { temp: 31, condition: 'Partly Cloudy', water: 27 },
            aug: { temp: 30, condition: 'Partly Cloudy', water: 27 },
            sep: { temp: 31, condition: 'Partly Cloudy', water: 27 },
            oct: { temp: 32, condition: 'Sunny', water: 27 },
            nov: { temp: 32, condition: 'Sunny', water: 25 },
            dec: { temp: 31, condition: 'Sunny', water: 23 }
        },
        sections: {
            thingsToDo: [
                {
                    id: 'gambia-river',
                    title: { en: 'Gambia River Cruise', sv: 'Kryssning på Gambiafloden' },
                    description: { en: 'Sail through mangroves and spot hippos, monkeys, and hundreds of bird species.', sv: 'Segla genom mangrover och se flodhästar, apor och hundratals fågelarter.' },
                    image: 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?q=80&w=800',
                    category: 'nature',
                    duration: 'Full day',
                    price: '€45'
                },
                {
                    id: 'kachikally',
                    title: { en: 'Kachikally Crocodile Pool', sv: 'Kachikally Krokodilpool' },
                    description: { en: 'Visit this sacred pool in Bakau where you can get close to docile crocodiles and learn about local legends.', sv: 'Besök denna heliga pool i Bakau där du kan komma nära fogliga krokodiler och lära dig om lokala legender.' },
                    image: 'https://images.unsplash.com/photo-1516972087541-b0db04b39ad3?q=80&w=800',
                    category: 'culture',
                    duration: '1-2 hours',
                    price: '€5'
                },
                {
                    id: 'bijilo-forest',
                    title: { en: 'Bijilo Forest Park', sv: 'Bijilo Forest Park' },
                    description: { en: 'A beautiful nature reserve known as Monkey Park. Walk the trails and see Green Vervet and Red Colobus monkeys.', sv: 'Ett vackert naturreservat känt som Monkey Park. Vandra längs stigarna och se gröna markattor och röda colobus-apor.' },
                    image: 'https://images.unsplash.com/photo-1543152744-8df63428d052?q=80&w=800',
                    category: 'nature',
                    duration: '2 hours',
                    price: '€3'
                },
                {
                    id: 'abuko',
                    title: { en: 'Abuko Nature Reserve', sv: 'Abuko Naturreservat' },
                    description: { en: 'Gambia’s first nature reserve, a birdwatcher’s paradise with lush tropical vegetation and diverse wildlife.', sv: 'Gambias första naturreservat, ett paradis för fågelskådare med frodig tropisk vegetation och rikt djurliv.' },
                    image: 'https://images.unsplash.com/photo-1452570053594-1b985d6ea890?q=80&w=800',
                    category: 'nature',
                    duration: 'Half day',
                    price: '€10'
                }
            ],
            restaurants: [
                {
                    id: 'ngala-lodge',
                    name: 'Ngala Lodge Restaurant',
                    description: { en: 'Fine dining with spectacular ocean views. Famous for its romantic atmosphere and excellent international cuisine.', sv: 'Fine dining med spektakulär havsutsikt. Känd för sin romantiska atmosfär och utmärkta internationella kök.' },
                    image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=800',
                    cuisine: 'fine-dining',
                    priceRange: '$$$$'
                },
                {
                    id: 'el-sol',
                    name: 'El Sol',
                    description: { en: 'Vibrant Mexican cuisine and tapas in the heart of Kololi. Great cocktails and lively atmosphere.', sv: 'Livlig mexikansk mat och tapas i hjärtat av Kololi. Fantastiska cocktails och härlig stämning.' },
                    image: 'https://images.unsplash.com/photo-1565557623262-b51c2513a641?q=80&w=800',
                    cuisine: 'casual',
                    priceRange: '$$'
                },
                {
                    id: 'paradise-beach',
                    name: 'Paradise Beach Bar',
                    description: { en: 'Classic beach bar in Kotu serving fresh seafood and cold drinks right on the sand.', sv: 'Klassisk strandbar i Kotu som serverar färska skaldjur och kalla drycker direkt på sanden.' },
                    image: 'https://images.unsplash.com/photo-1533619043865-1c2e2f32ff5f?q=80&w=800',
                    cuisine: 'beach-bar',
                    priceRange: '$$'
                }
            ],
            attractions: [], // Kept empty as ThingsToDo covers most
            itineraries: [],
            practicalInfo: {
                currency: { code: 'GMD', name: { en: 'Dalasi', sv: 'Dalasi' } },
                language: { en: 'English', sv: 'Engelska' },
                timezone: 'GMT+0',
                visaInfo: { en: 'Visa-free for many nationalities. Check current requirements.', sv: 'Visumfritt för många nationaliteter. Kontrollera aktuella krav.' },
                bestTimeToVisit: { en: 'November to May (Dry season) is ideal for sun and wildlife.', sv: 'November till maj (torrperiod) är idealiskt för sol och djurliv.' },
                transport: [
                    { title: { en: 'Taxi', sv: 'Taxi' }, description: { en: 'Green tourist taxis are safe and reliable.', sv: 'Gröna turisttaxibilar är säkra och pålitliga.' }, icon: 'directions_car' }
                ],
                tips: {
                    en: ['Bring insect repellent', 'Exchange currency locally for better rates', 'Always haggle at markets like Royal Albert'],
                    sv: ['Ta med myggmedel', 'Växla valuta lokalt för bättre kurser', 'Pruta alltid på marknader som Royal Albert']
                }
            }
        }
    },
    {
        id: '1',
        destinationId: '117',
        slug: 'gran-canaria',
        title: "Gran Canaria, Spain",
        titleSv: "Gran Canaria, Spanien",
        imageUrl: "https://images.pexels.com/photos/1319515/pexels-photo-1319515.jpeg?auto=compress&cs=tinysrgb&w=1600",
        price: "Fr. 5500 kr",
        type: "Flight + Hotel",
        isActive: true,
        isFeatured: true,
        landingPage: {
            heroImage: "https://images.pexels.com/photos/1319515/pexels-photo-1319515.jpeg?auto=compress&cs=tinysrgb&w=1600",
            description: {
                en: "Discover the miniature continent of Gran Canaria. From the dunes of Maspalomas to the lush interior mountains, experience a holiday of diversity. Perfect for families and sun-seekers alike.",
                sv: "Upptäck miniatyrkontinenten Gran Canaria. Från sanddynerna i Maspalomas till de frodiga inre bergen, upplev en semester fylld av mångfald. Perfekt för familjer och solbadare."
            },
            featuredHotels: []
        },
        richContent: {
            history: {
                en: "Gran Canaria has a fascinating history dating back to the Guanches, the indigenous aboriginal people who lived in caves. The island was conquered by the Kingdom of Castille in the 15th century, a pivotal moment that turned it into a crucial stopover for Christopher Columbus on his voyages to the Americas. Today, the colonial architecture of Vegueta in Las Palmas stands as a testament to this rich past.",
                sv: "Gran Canaria har en fascinerande historia som går tillbaka till guancherna, det ursprungliga folket som bodde i grottor. Ön erövrades av Kastilien på 1400-talet, ett avgörande ögonblick som gjorde den till en viktig mellanlandning för Christofer Columbus på hans resor till Amerika. Idag står den koloniala arkitekturen i Vegueta i Las Palmas som ett bevis på detta rika förflutna."
            },
            nature: {
                en: "Often called a 'Miniature Continent', Gran Canaria offers incredibly diverse landscapes. The south is famous for the golden Dunes of Maspalomas, a desert-like nature reserve. Moving inland, the terrain rises sharply into rugged volcanic mountains like Roque Nublo, surrounded by pine forests and ancient calderas. The northern coast is lush, green, and dramatic, offering a stark contrast to the arid south.",
                sv: "Ofta kallad en 'Miniatyrkontinent', erbjuder Gran Canaria otroligt varierande landskap. Södern är känd för de gyllene sanddynerna i Maspalomas, ett ökenliknande naturreservat. Inåt landet stiger terrängen kraftigt till robusta vulkaniska berg som Roque Nublo, omgivna av tallskogar och gamla kalderor. Den norra kusten är frodig, grön och dramatisk, vilket erbjuder en stark kontrast till den torra södern."
            }
        },
        gallery: [
            "https://images.unsplash.com/photo-1542259659439-d227c2eb2299?q=80&w=1200",
            "https://images.unsplash.com/photo-1621323323087-3c7263309a47?q=80&w=1200",
            "https://images.unsplash.com/photo-1596236560424-37a1109a454e?q=80&w=1200",
            "https://images.unsplash.com/photo-1516483638261-f4dbaf036963?q=80&w=1200",
            "https://images.unsplash.com/photo-1533630252329-87a3cb2d2c7b?q=80&w=1200"
        ],
        weather: {
            jan: { temp: 21, condition: 'Sunny', water: 19 },
            feb: { temp: 21, condition: 'Sunny', water: 19 },
            mar: { temp: 23, condition: 'Sunny', water: 19 },
            apr: { temp: 24, condition: 'Sunny', water: 20 },
            may: { temp: 25, condition: 'Sunny', water: 20 },
            jun: { temp: 27, condition: 'Sunny', water: 21 },
            jul: { temp: 29, condition: 'Sunny', water: 22 },
            aug: { temp: 29, condition: 'Sunny', water: 23 },
            sep: { temp: 28, condition: 'Sunny', water: 23 },
            oct: { temp: 27, condition: 'Partly Cloudy', water: 23 },
            nov: { temp: 24, condition: 'Sunny', water: 21 },
            dec: { temp: 22, condition: 'Sunny', water: 20 }
        },
        sections: {
            thingsToDo: [
                {
                    id: 'gc-dunes',
                    title: { en: 'Maspalomas Dunes', sv: 'Maspalomas Sanddyner' },
                    description: { en: 'Explore the iconic golden sand dunes stretching to the ocean. A natural reserve perfect for walking and sunset views.', sv: 'Utforska de ikoniska gyllene sanddynerna som sträcker sig mot havet. Ett naturreservat perfekt för promenader och solnedgångar.' },
                    image: 'https://images.unsplash.com/photo-1558981852-426c6c22a060?q=80&w=800&auto=format&fit=crop',
                    category: 'nature',
                    duration: '2-3 hours',
                    price: 'Free'
                },
                {
                    id: 'gc-roque',
                    title: { en: 'Roque Nublo Hike', sv: 'Roque Nublo Vandring' },
                    description: { en: 'Hike to Gran Canaria\'s most famous volcanic rock formation with panoramic views across the island.', sv: 'Vandra till Gran Canarias mest berömda vulkaniska klippformation med panoramautsikt över ön.' },
                    image: 'https://images.unsplash.com/photo-1596236560424-37a1109a454e?q=80&w=800&auto=format&fit=crop',
                    category: 'adventure',
                    duration: '3-4 hours',
                    price: 'Free'
                },
                {
                    id: 'gc-vegueta',
                    title: { en: 'Explore Vegueta Old Town', sv: 'Utforska Vegueta Gamla Stan' },
                    description: { en: 'Walk through the UNESCO-listed historic quarter with colonial architecture and the Columbus House museum.', sv: 'Promenera genom det UNESCO-listade historiska kvarteret med kolonial arkitektur och Columbus-huset.' },
                    image: 'https://images.unsplash.com/photo-1559128010-7c1ad6e1b6a5?q=80&w=800&auto=format&fit=crop',
                    category: 'culture',
                    duration: '2-3 hours',
                    price: 'Free'
                },
                {
                    id: 'gc-poema',
                    title: { en: 'Poema del Mar Aquarium', sv: 'Poema del Mar Akvarium' },
                    description: { en: 'Visit Europe\'s most impressive aquarium with the world\'s largest curved glass window.', sv: 'Besök Europas mest imponerande akvarium med världens största buktade glasfönster.' },
                    image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?q=80&w=800&auto=format&fit=crop',
                    category: 'family',
                    duration: '2-3 hours',
                    price: '€23'
                },
                {
                    id: 'gc-mogán',
                    title: { en: 'Puerto de Mogán Visit', sv: 'Besök Puerto de Mogán' },
                    description: { en: 'Discover "Little Venice" - a picturesque fishing village with colorful bougainvillea and canal-side restaurants.', sv: 'Upptäck "Lilla Venedig" - en pittoresk fiskeby med färgglad bougainvillea och restauranger vid kanaler.' },
                    image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=800&auto=format&fit=crop',
                    category: 'culture',
                    duration: 'Half day',
                    price: 'Free'
                }
            ],
            attractions: [
                {
                    id: 'attr-gc-dunes',
                    title: { en: 'Maspalomas Dunes Natural Reserve', sv: 'Maspalomas Sanddyner Naturreservat' },
                    description: { en: 'A stunning 400-hectare desert ecosystem with golden dunes, palm oases, and diverse birdlife.', sv: 'Ett fantastiskt 400 hektar stort ökenekosystem med gyllene sanddyner, palmoaser och varierat fågelliv.' },
                    image: 'https://images.unsplash.com/photo-1558981852-426c6c22a060?q=80&w=800&auto=format&fit=crop',
                    category: 'nature',
                    address: 'Maspalomas, 35100 Gran Canaria'
                },
                {
                    id: 'attr-gc-palmitos',
                    title: { en: 'Palmitos Park', sv: 'Palmitos Park' },
                    description: { en: 'Botanical garden and zoo featuring dolphins, birds, and exotic animals in a beautiful valley setting.', sv: 'Botanisk trädgård och djurpark med delfiner, fåglar och exotiska djur i en vacker dalmiljö.' },
                    image: 'https://images.unsplash.com/photo-1437622368342-7a3d73a34c8f?q=80&w=800&auto=format&fit=crop',
                    category: 'family',
                    entryFee: '€34'
                },
                {
                    id: 'attr-gc-tejeda',
                    title: { en: 'Tejeda Village', sv: 'Tejeda By' },
                    description: { en: 'Named one of Spain\'s most beautiful villages, perched in the mountains with stunning caldera views.', sv: 'Utsedd till en av Spaniens vackraste byar, belägen i bergen med fantastisk utsikt över kalderan.' },
                    image: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?q=80&w=800&auto=format&fit=crop',
                    category: 'scenic'
                },
                {
                    id: 'attr-gc-playa',
                    title: { en: 'Playa de Amadores', sv: 'Playa de Amadores' },
                    description: { en: 'Crystal-clear turquoise waters and golden sand make this one of the island\'s best beaches.', sv: 'Kristallklart turkost vatten och gyllene sand gör detta till en av öns bästa stränder.' },
                    image: 'https://images.unsplash.com/photo-1519046904884-53103b34b206?q=80&w=800&auto=format&fit=crop',
                    category: 'beach'
                }
            ],
            restaurants: [
                {
                    id: 'rest-gc-tagoror',
                    name: 'El Tagoror',
                    description: { en: 'Traditional Canarian cuisine in a cave restaurant offering authentic local dishes.', sv: 'Traditionell kanarisk mat i en grottrestaurang med autentiska lokala rätter.' },
                    image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=800',
                    cuisine: 'Canarian',
                    priceRange: '$$',
                    rating: 4.7
                },
                {
                    id: 'rest-gc-qué-tal',
                    name: 'Qué Tal by Stena',
                    description: { en: 'Modern fusion restaurant with stunning rooftop views over Las Palmas harbor.', sv: 'Modern fusionrestaurang med fantastisk taksikt över Las Palmas hamn.' },
                    image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=800',
                    cuisine: 'Fusion',
                    priceRange: '$$$',
                    rating: 4.8
                },
                {
                    id: 'rest-gc-grill',
                    name: 'Nelson\'s Restaurant',
                    description: { en: 'Fresh seafood grilled to perfection with ocean views in Puerto de Mogán.', sv: 'Färsk skaldjur grillad till perfektion med havsutsikt i Puerto de Mogán.' },
                    image: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=800',
                    cuisine: 'Seafood',
                    priceRange: '$$$',
                    rating: 4.6
                }
            ],
            itineraries: [
                {
                    id: 'itin-gc-1day',
                    title: { en: '1 Day in Gran Canaria', sv: '1 Dag på Gran Canaria' },
                    description: { en: 'Experience the highlights from beach to mountains in one perfect day.', sv: 'Upplev höjdpunkterna från strand till berg på en perfekt dag.' },
                    image: 'https://images.unsplash.com/photo-1596236560424-37a1109a454e?q=80&w=800&auto=format&fit=crop',
                    days: 1,
                    highlights: {
                        en: ['Morning at Maspalomas Dunes', 'Drive to Roque Nublo', 'Lunch in Tejeda', 'Sunset at Puerto de Mogán'],
                        sv: ['Morgon vid Maspalomas sanddyner', 'Kör till Roque Nublo', 'Lunch i Tejeda', 'Solnedgång i Puerto de Mogán']
                    }
                },
                {
                    id: 'itin-gc-3day',
                    title: { en: '3 Days in Gran Canaria', sv: '3 Dagar på Gran Canaria' },
                    description: { en: 'Discover beaches, mountains, and charming towns at a relaxed pace.', sv: 'Upptäck stränder, berg och charmiga städer i avslappnad takt.' },
                    image: 'https://images.unsplash.com/photo-1558981852-426c6c22a060?q=80&w=800&auto=format&fit=crop',
                    days: 3,
                    highlights: {
                        en: ['Day 1: Las Palmas & Vegueta', 'Day 2: Mountain villages & Roque Nublo', 'Day 3: Southern beaches & dunes'],
                        sv: ['Dag 1: Las Palmas & Vegueta', 'Dag 2: Bergsbyar & Roque Nublo', 'Dag 3: Södra stränderna & dynerna']
                    }
                },
                {
                    id: 'itin-gc-7day',
                    title: { en: '7 Days in Gran Canaria', sv: '7 Dagar på Gran Canaria' },
                    description: { en: 'The complete island experience with hidden gems and local experiences.', sv: 'Den kompletta öupplevelsen med gömda pärlor och lokala upplevelser.' },
                    image: 'https://images.unsplash.com/photo-1542259659439-d227c2eb2299?q=80&w=800&auto=format&fit=crop',
                    days: 7,
                    highlights: {
                        en: ['Days 1-2: Las Palmas exploration', 'Day 3: Northern coast drive', 'Days 4-5: Mountain adventure', 'Days 6-7: Beach relaxation'],
                        sv: ['Dag 1-2: Upptäck Las Palmas', 'Dag 3: Norra kustvägen', 'Dag 4-5: Bergsäventyr', 'Dag 6-7: Strandavkoppling']
                    }
                }
            ],
            practicalInfo: {
                currency: { code: 'EUR', name: { en: 'Euro', sv: 'Euro' } },
                language: { en: 'Spanish (English widely spoken in tourist areas)', sv: 'Spanska (engelska talas allmänt i turistområden)' },
                timezone: 'GMT+0 (WET)',
                visaInfo: { en: 'EU citizens: No visa required. Others: Schengen visa rules apply.', sv: 'EU-medborgare: Inget visum krävs. Övriga: Schengen-visumregler gäller.' },
                bestTimeToVisit: { en: 'Year-round destination. Nov-Mar is warmest. Apr-Jun and Sep-Oct ideal for hiking.', sv: 'Året-runt-destination. Nov-mar är varmast. Apr-jun och sep-okt idealiskt för vandring.' },
                transport: [
                    { title: { en: 'Airport Transfer', sv: 'Flygplatstransfer' }, description: { en: 'Gran Canaria Airport (LPA) is 25km from Las Palmas. Buses, taxis and car rentals available.', sv: 'Gran Canaria flygplats (LPA) ligger 25 km från Las Palmas. Bussar, taxi och hyrbil tillgängligt.' }, icon: 'flight' },
                    { title: { en: 'Public Transport', sv: 'Kollektivtrafik' }, description: { en: 'Global bus network covers the island. Buy a TransGC card for discounts.', sv: 'Global bussnät täcker ön. Köp TransGC-kort för rabatter.' }, icon: 'directions_bus' },
                    { title: { en: 'Car Rental', sv: 'Hyrbil' }, description: { en: 'Recommended for exploring mountains and villages. Many agencies at the airport.', sv: 'Rekommenderas för att utforska bergen och byarna. Många uthyrare på flygplatsen.' }, icon: 'directions_car' }
                ],
                tips: {
                    en: ['Rent a car for interior exploration', 'Try local papas arrugadas with mojo sauce', 'Book Poema del Mar tickets online', 'Visit Vegueta market on Sunday', 'Bring layers for mountain trips'],
                    sv: ['Hyr bil för att utforska inlandet', 'Prova lokala papas arrugadas med mojosås', 'Boka Poema del Mar-biljetter online', 'Besök Vegueta-marknaden på söndag', 'Ta med lager för bergsutflykter']
                }
            }
        }
    },
    {
        id: '2',
        destinationId: '6',
        slug: 'antalya',
        title: "Antalya, Turkey",
        titleSv: "Antalya, Turkiet",
        imageUrl: "https://images.pexels.com/photos/3732498/pexels-photo-3732498.jpeg?auto=compress&cs=tinysrgb&w=1600",
        price: "Fr. 4200 kr",
        type: "Flight + Hotel",
        isActive: true,
        isFeatured: true,
        landingPage: {
            heroImage: "https://images.pexels.com/photos/3732498/pexels-photo-3732498.jpeg?auto=compress&cs=tinysrgb&w=1600",
            description: {
                en: "The pearl of the Turkish Riviera. Stunning beaches, ancient ruins, and world-class luxury resorts await in Antalya. Enjoy the perfect blend of history and relaxation.",
                sv: "Pärlan på den turkiska rivieran. Fantastiska stränder, antika ruiner och lyxresorter i världsklass väntar i Antalya. Njut av den perfekta blandningen av historia och avkoppling."
            },
            featuredHotels: []
        },
        richContent: {
            history: {
                en: "Founded as 'Attaleia' by Attalos II, King of Pergamon, in 150 BC, Antalya has been a jewel of the Mediterranean for millennia. It served as a major Roman port, visited by Emperor Hadrian (whose magnificent gate still stands). Through Byzantine and Ottoman rule, the city flourished, leaving behind a charming old town, Kaleiçi, with its narrow winding streets and historic wooden houses.",
                sv: "Grundat som 'Attaleia' av Attalos II, kung av Pergamon, 150 f.Kr., har Antalya varit en juvel i Medelhavet i årtusenden. Det fungerade som en stor romersk hamn och besöktes av kejsar Hadrianus (vars magnifika port fortfarande står kvar). Genom bysantinskt och ottomanskt styre blomstrade staden och lämnade efter sig en charmig gammal stad, Kaleiçi, med sina smala slingrande gator och historiska trähus."
            },
            nature: {
                en: "Antalya is where the majestic Taurus Mountains meet the turquoise Mediterranean Sea. The region is famed for the Düden and Kursunlu waterfalls, which cascade dramatically into rivers and the sea. The coastline features stunning cliffs, secluded coves, and long stretches of pebble and sand beaches, backed by pine forests that stay green all year round.",
                sv: "Antalya är där de majestätiska Taurusbergen möter det turkosa Medelhavet. Regionen är känd för vattenfallen Düden och Kursunlu, som faller dramatiskt ner i floder och havet. Kustlinjen har fantastiska klippor, avskilda vikar och långa sträckor av sten- och sandstränder, uppbackade av tallskogar som är gröna året runt."
            }
        },
        gallery: [
            "https://images.unsplash.com/photo-1542051841857-5f90071e7989?q=80&w=1200",
            "https://images.unsplash.com/photo-1599382292350-c793ba97d4b4?q=80&w=1200",
            "https://images.unsplash.com/photo-1630349193328-972173552db6?q=80&w=1200",
            "https://images.unsplash.com/photo-1605648916361-9bc12ad6a569?q=80&w=1200"
        ],
        weather: {
            jan: { temp: 15, condition: 'Partly Cloudy', water: 17 },
            feb: { temp: 16, condition: 'Rain', water: 17 },
            mar: { temp: 18, condition: 'Sunny', water: 17 },
            apr: { temp: 21, condition: 'Sunny', water: 18 },
            may: { temp: 26, condition: 'Sunny', water: 20 },
            jun: { temp: 31, condition: 'Sunny', water: 24 },
            jul: { temp: 34, condition: 'Sunny', water: 27 },
            aug: { temp: 34, condition: 'Sunny', water: 28 },
            sep: { temp: 31, condition: 'Sunny', water: 27 },
            oct: { temp: 26, condition: 'Sunny', water: 24 },
            nov: { temp: 20, condition: 'Partly Cloudy', water: 21 },
            dec: { temp: 16, condition: 'Rain', water: 19 }
        },
        sections: {
            thingsToDo: [
                {
                    id: 'antalya-duden',
                    title: { en: 'Düden Waterfalls', sv: 'Düden Vattenfall' },
                    description: { en: 'Marvel at the spectacular Lower Düden Waterfall cascading directly into the Mediterranean Sea.', sv: 'Beundra det spektakulära Nedre Düden-vattenfallet som faller direkt ner i Medelhavet.' },
                    image: 'https://images.unsplash.com/photo-1432405972618-c60b0225b8f9?q=80&w=800&auto=format&fit=crop',
                    category: 'nature',
                    duration: '2-3 hours',
                    price: 'Free'
                },
                {
                    id: 'antalya-kaleici',
                    title: { en: 'Explore Kaleiçi Old Town', sv: 'Utforska Kaleiçi Gamla Stan' },
                    description: { en: 'Wander through narrow cobblestone streets, Ottoman-era houses, and charming boutique shops.', sv: 'Vandra genom smala kullerstensgator, ottomanska hus och charmiga boutiquebutiker.' },
                    image: 'https://images.unsplash.com/photo-1566438480900-0609be27a4be?q=80&w=800&auto=format&fit=crop',
                    category: 'culture',
                    duration: '3-4 hours',
                    price: 'Free'
                },
                {
                    id: 'antalya-cable',
                    title: { en: 'Tunektepe Cable Car', sv: 'Tunektepe Linbana' },
                    description: { en: 'Enjoy breathtaking panoramic views of Antalya and the coastline from the cable car.', sv: 'Njut av fantastiska panoramavyer över Antalya och kusten från linbanan.' },
                    image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?q=80&w=800&auto=format&fit=crop',
                    category: 'adventure',
                    duration: '2 hours',
                    price: '€15'
                },
                {
                    id: 'antalya-boat',
                    title: { en: 'Kekova Boat Trip', sv: 'Kekova Båttur' },
                    description: { en: 'Discover the sunken city of Kekova and swim in crystal-clear waters on a full-day boat tour.', sv: 'Upptäck den sjunkna staden Kekova och simma i kristallklart vatten på en heldagstur.' },
                    image: 'https://images.unsplash.com/photo-1500514966906-fe245eea9344?q=80&w=800&auto=format&fit=crop',
                    category: 'adventure',
                    duration: 'Full day',
                    price: '€40-60'
                },
                {
                    id: 'antalya-museum',
                    title: { en: 'Antalya Museum', sv: 'Antalya Museum' },
                    description: { en: 'One of Turkey\'s largest museums with stunning Roman sculptures and archaeological treasures.', sv: 'Ett av Turkiets största museer med fantastiska romerska skulpturer och arkeologiska skatter.' },
                    image: 'https://images.unsplash.com/photo-1565060169194-19fabf3f6b82?q=80&w=800&auto=format&fit=crop',
                    category: 'culture',
                    duration: '2-3 hours',
                    price: '€5'
                },
                {
                    id: 'antalya-perge',
                    title: { en: 'Perge Ancient City', sv: 'Perge Antika Stad' },
                    description: { en: 'Explore the remarkably preserved ruins of one of the ancient world\'s greatest cities.', sv: 'Utforska de remarkabelt bevarade ruinerna av en av antikens största städer.' },
                    image: 'https://images.unsplash.com/photo-1564507592333-c60657eb6d6e?q=80&w=800&auto=format&fit=crop',
                    category: 'culture',
                    duration: '3-4 hours',
                    price: '€8'
                }
            ],
            attractions: [
                {
                    id: 'attr-hadrian',
                    title: { en: 'Hadrian\'s Gate', sv: 'Hadrianus Port' },
                    description: { en: 'A beautifully preserved Roman triumphal arch from 130 AD, marking the entrance to the old town.', sv: 'En vackert bevarad romersk triumfbåge från 130 e.Kr., som markerar ingången till gamla stan.' },
                    image: 'https://images.unsplash.com/photo-1569180880150-df4eed93c90b?q=80&w=800&auto=format&fit=crop',
                    category: 'historic',
                    address: 'Barbaros, 07100 Muratpaşa/Antalya'
                },
                {
                    id: 'attr-aspendos',
                    title: { en: 'Aspendos Theatre', sv: 'Aspendos Teater' },
                    description: { en: 'The best-preserved ancient theatre in the world, still hosting performances today.', sv: 'Den bäst bevarade antika teatern i världen, som fortfarande används för föreställningar.' },
                    image: 'https://images.unsplash.com/photo-1603565816030-6b389eeb23cb?q=80&w=800&auto=format&fit=crop',
                    category: 'historic',
                    entryFee: '€10'
                },
                {
                    id: 'attr-lara',
                    title: { en: 'Lara Beach', sv: 'Lara Strand' },
                    description: { en: 'A stunning 12km golden sand beach with crystal-clear waters and luxury resorts.', sv: 'En fantastisk 12 km lång gyllene sandstrand med kristallklart vatten och lyxiga resorter.' },
                    image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=800&auto=format&fit=crop',
                    category: 'beach'
                },
                {
                    id: 'attr-konyaalti',
                    title: { en: 'Konyaaltı Beach', sv: 'Konyaaltı Strand' },
                    description: { en: 'A beautiful pebble beach backed by the dramatic Taurus Mountains.', sv: 'En vacker stenstrand med de dramatiska Taurusbergen i bakgrunden.' },
                    image: 'https://images.unsplash.com/photo-1520454974749-611b7248ffdb?q=80&w=800&auto=format&fit=crop',
                    category: 'beach'
                },
                {
                    id: 'attr-olympos',
                    title: { en: 'Olympos & Chimaera', sv: 'Olympos & Chimayra' },
                    description: { en: 'Ancient Lycian ruins combined with eternal flames emerging from the mountainside.', sv: 'Antika lykiska ruiner kombinerat med eviga lågor som stiger från bergsidan.' },
                    image: 'https://images.unsplash.com/photo-1518548419970-58e3b4079ab2?q=80&w=800&auto=format&fit=crop',
                    category: 'historic'
                }
            ],
            restaurants: [
                {
                    id: 'rest-seraser',
                    name: 'Seraser Fine Dining',
                    description: { en: 'Award-winning restaurant in a historic Ottoman mansion, offering Turkish-Mediterranean cuisine.', sv: 'Prisbelönad restaurang i ett historiskt ottomanskt herrgård med turkisk-medelhavskök.' },
                    image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=800',
                    cuisine: 'Turkish-Mediterranean',
                    priceRange: '$$$$',
                    rating: 4.8
                },
                {
                    id: 'rest-vanilla',
                    name: 'Vanilla Lounge',
                    description: { en: 'Elegant rooftop restaurant with stunning sea views and international cuisine.', sv: 'Elegant takrestaurang med fantastisk havsutsikt och internationellt kök.' },
                    image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=800',
                    cuisine: 'International',
                    priceRange: '$$$',
                    rating: 4.6
                },
                {
                    id: 'rest-7mehmet',
                    name: '7 Mehmet Restaurant',
                    description: { en: 'Traditional Turkish dishes in a beautiful garden setting since 1960.', sv: 'Traditionella turkiska rätter i en vacker trädgårdsmiljö sedan 1960.' },
                    image: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=800',
                    cuisine: 'Traditional Turkish',
                    priceRange: '$$',
                    rating: 4.7
                },
                {
                    id: 'rest-arma',
                    name: 'Arma Restaurant',
                    description: { en: 'Seafood restaurant in a historic building overlooking the old harbor.', sv: 'Fiskrestaurang i en historisk byggnad med utsikt över den gamla hamnen.' },
                    image: 'https://images.unsplash.com/photo-1559339352-11d035aa65de?q=80&w=800',
                    cuisine: 'Seafood',
                    priceRange: '$$$',
                    rating: 4.5
                }
            ],
            itineraries: [
                {
                    id: 'itin-1day',
                    title: { en: '1 Day in Antalya', sv: '1 Dag i Antalya' },
                    description: { en: 'Experience the best of the old town, museum, and waterfall in a perfect day.', sv: 'Upplev det bästa av gamla stan, museet och vattenfallet på en perfekt dag.' },
                    image: 'https://images.unsplash.com/photo-1527838832700-5059252407fa?q=80&w=800&auto=format&fit=crop',
                    days: 1,
                    highlights: {
                        en: ['Morning walk in Kaleiçi', 'Antalya Museum visit', 'Lunch at Old Harbor', 'Düden Waterfall sunset'],
                        sv: ['Morgonpromenad i Kaleiçi', 'Besök Antalya Museum', 'Lunch vid Gamla Hamnen', 'Düden Vattenfall i solnedgången']
                    }
                },
                {
                    id: 'itin-3day',
                    title: { en: '3 Days in Antalya', sv: '3 Dagar i Antalya' },
                    description: { en: 'A perfect balance of history, nature, and beach relaxation.', sv: 'En perfekt balans mellan historia, natur och strandavkoppling.' },
                    image: 'https://images.unsplash.com/photo-1530841377377-3ff06c0ca713?q=80&w=800&auto=format&fit=crop',
                    days: 3,
                    highlights: {
                        en: ['Day 1: Kaleiçi & Museum', 'Day 2: Perge & Aspendos', 'Day 3: Beach & Cable Car'],
                        sv: ['Dag 1: Kaleiçi & Museum', 'Dag 2: Perge & Aspendos', 'Dag 3: Strand & Linbana']
                    }
                },
                {
                    id: 'itin-7day',
                    title: { en: '7 Days in Antalya', sv: '7 Dagar i Antalya' },
                    description: { en: 'The ultimate Antalya experience with day trips and hidden gems.', sv: 'Den ultimata Antalya-upplevelsen med dagsutflykter och gömda pärlor.' },
                    image: 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?q=80&w=800&auto=format&fit=crop',
                    days: 7,
                    highlights: {
                        en: ['Days 1-2: City exploration', 'Day 3: Kekova boat trip', 'Day 4: Olympos & Chimaera', 'Days 5-6: Beach & Resort', 'Day 7: Shopping & Spa'],
                        sv: ['Dag 1-2: Stadsutforskning', 'Dag 3: Kekova båttur', 'Dag 4: Olympos & Chimayra', 'Dag 5-6: Strand & Resort', 'Dag 7: Shopping & Spa']
                    }
                }
            ],
            practicalInfo: {
                currency: { code: 'TRY', name: { en: 'Turkish Lira', sv: 'Turkisk Lira' } },
                language: { en: 'Turkish (English widely spoken)', sv: 'Turkiska (engelska talas allmänt)' },
                timezone: 'GMT+3',
                visaInfo: { en: 'E-visa required for most nationalities. Apply online at evisa.gov.tr', sv: 'E-visum krävs för de flesta nationaliteter. Ansök online på evisa.gov.tr' },
                bestTimeToVisit: { en: 'April-June and September-October for mild weather. July-August for beach holidays.', sv: 'April-juni och september-oktober för milt väder. Juli-augusti för strandsemester.' },
                transport: [
                    { title: { en: 'Airport Transfer', sv: 'Flygplatstransfer' }, description: { en: 'Antalya Airport is 13km from city center. Shuttle buses and taxis available.', sv: 'Antalya flygplats ligger 13 km från centrum. Shuttlebussar och taxi finns.' }, icon: 'flight' },
                    { title: { en: 'Public Transport', sv: 'Kollektivtrafik' }, description: { en: 'Antray tram and bus network cover the city. Buy AntalyaKart for easy travel.', sv: 'Antray-spårvagn och bussnät täcker staden. Köp AntalyaKart för enkla resor.' }, icon: 'tram' },
                    { title: { en: 'Car Rental', sv: 'Hyrbil' }, description: { en: 'Recommended for day trips. Many rental companies at the airport.', sv: 'Rekommenderas för dagsutflykter. Många uthyrningsföretag på flygplatsen.' }, icon: 'directions_car' }
                ],
                tips: {
                    en: ['Bargaining is expected at bazaars', 'Try local dishes like pide and lahmacun', 'Carry cash for small shops', 'Book popular restaurants in advance', 'Respect dress codes at mosques'],
                    sv: ['Pruta på basarerna', 'Prova lokala rätter som pide och lahmacun', 'Ha kontanter för små butiker', 'Boka populära restauranger i förväg', 'Respektera klädkod vid moskéer']
                }
            }
        }
    },
    {
        id: '3',
        destinationId: '113',
        slug: 'rhodes',
        title: "Rhodes, Greece",
        titleSv: "Rhodos, Grekland",
        imageUrl: "https://images.pexels.com/photos/1285625/pexels-photo-1285625.jpeg?auto=compress&cs=tinysrgb&w=1600",
        price: "Fr. 4800 kr",
        type: "Flight + Hotel",
        isActive: true,
        isFeatured: true,
        landingPage: {
            heroImage: "https://images.pexels.com/photos/1285625/pexels-photo-1285625.jpeg?auto=compress&cs=tinysrgb&w=1600",
            description: {
                en: "A historical gem in the Dodecanese. Walk through the medieval Old Town, explore ancient acropolises, or simply relax on golden sandy beaches under the Aegean sun.",
                sv: "En historisk pärla i Dodekaneserna. Promenera genom den medeltida Gamla stan, utforska antika akropolis eller bara koppla av på gyllene sandstränder under Egeiska solen."
            },
            featuredHotels: []
        },
        gallery: [
            "https://images.unsplash.com/photo-1596568288591-64d8db19df51?q=80&w=1200",
            "https://images.unsplash.com/photo-1605333575459-7b3b3a372074?q=80&w=1200",
            "https://images.unsplash.com/photo-1506929562872-bb421503ef21?q=80&w=1200"
        ],
        weather: {
            jan: { temp: 15, condition: 'Partly Cloudy', water: 16 },
            feb: { temp: 15, condition: 'Sunny', water: 16 },
            mar: { temp: 17, condition: 'Sunny', water: 16 },
            apr: { temp: 20, condition: 'Sunny', water: 17 },
            may: { temp: 24, condition: 'Sunny', water: 20 },
            jun: { temp: 28, condition: 'Sunny', water: 22 },
            jul: { temp: 31, condition: 'Hot', water: 25 },
            aug: { temp: 31, condition: 'Hot', water: 26 },
            sep: { temp: 29, condition: 'Sunny', water: 25 },
            oct: { temp: 24, condition: 'Sunny', water: 23 },
            nov: { temp: 20, condition: 'Partly Cloudy', water: 20 },
            dec: { temp: 17, condition: 'Rain', water: 18 }
        },
        sections: {
            thingsToDo: [
                {
                    id: 'rhodes-old-town',
                    title: { en: 'Explore Rhodes Old Town', sv: 'Utforska Rhodos Gamla Stan' },
                    description: { en: 'Walk through the UNESCO World Heritage medieval city with the Palace of the Grand Master and Street of the Knights.', sv: 'Promenera genom den UNESCO-listade medeltidsstaden med Stormästarens palats och Riddarnas gata.' },
                    image: 'https://images.unsplash.com/photo-1596568288591-64d8db19df51?q=80&w=800&auto=format&fit=crop',
                    category: 'culture',
                    duration: '3-4 hours',
                    price: 'Free'
                },
                {
                    id: 'rhodes-lindos',
                    title: { en: 'Visit Lindos Acropolis', sv: 'Besök Lindos Akropolis' },
                    description: { en: 'Climb to the ancient acropolis perched above a whitewashed village and stunning bay.', sv: 'Klättra till den antika akropolis som tronar ovanför en vitkalkad by och fantastisk bukt.' },
                    image: 'https://images.unsplash.com/photo-1533105079780-92b9be482077?q=80&w=800&auto=format&fit=crop',
                    category: 'culture',
                    duration: 'Half day',
                    price: '€12'
                },
                {
                    id: 'rhodes-beach',
                    title: { en: 'Tsambika Beach Day', sv: 'Tsambika Stranddag' },
                    description: { en: 'Relax on one of the island\'s most beautiful golden sand beaches with crystal-clear waters.', sv: 'Koppla av på en av öns vackraste gyllene sandstränder med kristallklart vatten.' },
                    image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=800&auto=format&fit=crop',
                    category: 'beach',
                    duration: 'Full day',
                    price: 'Free'
                },
                {
                    id: 'rhodes-boat',
                    title: { en: 'Symi Island Day Trip', sv: 'Dagstur till Symi' },
                    description: { en: 'Take a boat trip to the colorful neoclassical harbor town of Symi.', sv: 'Ta en båttur till den färgglada neoklassiska hamnstaden Symi.' },
                    image: 'https://images.unsplash.com/photo-1568700942090-19dc36a68619?q=80&w=800&auto=format&fit=crop',
                    category: 'adventure',
                    duration: 'Full day',
                    price: '€45'
                }
            ],
            attractions: [
                {
                    id: 'attr-rhodes-palace',
                    title: { en: 'Palace of the Grand Master', sv: 'Stormästarens Palats' },
                    description: { en: 'A medieval castle and museum that was the headquarters of the Knights of St. John.', sv: 'Ett medeltida slott och museum som var högkvarter för Johanniterriddarna.' },
                    image: 'https://images.unsplash.com/photo-1605333575459-7b3b3a372074?q=80&w=800&auto=format&fit=crop',
                    category: 'historic',
                    entryFee: '€10'
                },
                {
                    id: 'attr-rhodes-valley',
                    title: { en: 'Valley of the Butterflies', sv: 'Fjärilarnas Dal' },
                    description: { en: 'A unique nature reserve where thousands of butterflies gather in summer.', sv: 'Ett unikt naturreservat där tusentals fjärilar samlas på sommaren.' },
                    image: 'https://images.unsplash.com/photo-1558981285-6f0c94958bb6?q=80&w=800&auto=format&fit=crop',
                    category: 'nature',
                    entryFee: '€5'
                },
                {
                    id: 'attr-rhodes-anthony',
                    title: { en: 'Anthony Quinn Bay', sv: 'Anthony Quinn-bukten' },
                    description: { en: 'A stunning secluded cove named after the actor, perfect for snorkeling.', sv: 'En fantastisk avskild vik uppkallad efter skådespelaren, perfekt för snorkling.' },
                    image: 'https://images.unsplash.com/photo-1519046904884-53103b34b206?q=80&w=800&auto=format&fit=crop',
                    category: 'beach'
                }
            ],
            restaurants: [
                {
                    id: 'rest-rhodes-mavrikos',
                    name: 'Mavrikos Restaurant',
                    description: { en: 'Award-winning fine dining in Lindos since 1933, serving creative Greek cuisine.', sv: 'Prisbelönt finrestaurang i Lindos sedan 1933 som serverar kreativt grekiskt kök.' },
                    image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=800',
                    cuisine: 'Greek Fine Dining',
                    priceRange: '$$$$',
                    rating: 4.8
                },
                {
                    id: 'rest-rhodes-tamam',
                    name: 'To Steno',
                    description: { en: 'Traditional taverna hidden in the Old Town serving authentic local dishes.', sv: 'Traditionell taverna gömd i Gamla stan som serverar autentiska lokala rätter.' },
                    image: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=800',
                    cuisine: 'Traditional Greek',
                    priceRange: '$$',
                    rating: 4.7
                }
            ],
            itineraries: [
                {
                    id: 'itin-rhodes-3day',
                    title: { en: '3 Days in Rhodes', sv: '3 Dagar på Rhodos' },
                    description: { en: 'Perfect introduction to the island\'s history, beaches, and villages.', sv: 'Perfekt introduktion till öns historia, stränder och byar.' },
                    image: 'https://images.unsplash.com/photo-1596568288591-64d8db19df51?q=80&w=800&auto=format&fit=crop',
                    days: 3,
                    highlights: {
                        en: ['Day 1: Rhodes Old Town exploration', 'Day 2: Lindos & East Coast beaches', 'Day 3: Symi boat trip'],
                        sv: ['Dag 1: Utforska Rhodos Gamla Stan', 'Dag 2: Lindos & östkustens stränder', 'Dag 3: Båttur till Symi']
                    }
                }
            ],
            practicalInfo: {
                currency: { code: 'EUR', name: { en: 'Euro', sv: 'Euro' } },
                language: { en: 'Greek (English widely spoken)', sv: 'Grekiska (engelska talas allmänt)' },
                timezone: 'GMT+2 (EET)',
                visaInfo: { en: 'EU citizens: No visa required. Others: Schengen visa.', sv: 'EU-medborgare: Inget visum krävs. Övriga: Schengen-visum.' },
                bestTimeToVisit: { en: 'May-October for beach weather. April and October ideal for sightseeing.', sv: 'Maj-oktober för strandväder. April och oktober idealiskt för sightseeing.' },
                transport: [
                    { title: { en: 'Airport', sv: 'Flygplats' }, description: { en: 'Rhodes Airport (RHO) is 14km from Rhodes Town. Buses and taxis available.', sv: 'Rhodos flygplats (RHO) ligger 14 km från Rhodos stad. Bussar och taxi finns.' }, icon: 'flight' },
                    { title: { en: 'Car Rental', sv: 'Hyrbil' }, description: { en: 'Recommended for exploring the island. Book in advance for summer.', sv: 'Rekommenderas för att utforska ön. Boka i förväg för sommaren.' }, icon: 'directions_car' }
                ],
                tips: {
                    en: ['Wear comfortable shoes for cobblestones in Old Town', 'Book Lindos accommodation early', 'Try local honey and olive oil'],
                    sv: ['Ha bekväma skor för kullerstensgatorna i Gamla stan', 'Boka boende i Lindos tidigt', 'Prova lokal honung och olivolja']
                }
            }
        }
    },

    {
        id: '4',
        destinationId: '28',
        slug: 'hurghada',
        title: "Hurghada, Egypt",
        titleSv: "Hurghada, Egypten",
        imageUrl: "https://images.pexels.com/photos/3290075/pexels-photo-3290075.jpeg?auto=compress&cs=tinysrgb&w=1600",
        price: "Fr. 3900 kr",
        type: "All Inclusive",
        isActive: true,
        isFeatured: true,
        landingPage: {
            heroImage: "https://images.pexels.com/photos/3290075/pexels-photo-3290075.jpeg?auto=compress&cs=tinysrgb&w=1600",
            description: {
                en: "A diver's paradise on the Red Sea coast. Crystal clear waters, colorful coral reefs, and year-round sunshine make Hurghada a top choice for winter sun.",
                sv: "Ett paradis för dykare vid Röda havets kust. Kristallklart vatten, färgglada korallrev och solsken året runt gör Hurghada till ett toppval för vintersol."
            },
            featuredHotels: []
        },
        richContent: {
            history: {
                en: "Once a small fishing village, Hurghada was founded in the early 20th century. It remained relatively isolated until the 1980s when the investment in tourism transformed it into one of the world's leading seaside resorts. Despite its modernization, the old town of El Dahar retains its traditional Egyptian charm with bustling souks and coffee shops.",
                sv: "Hurghada var en gång en liten fiskeby som grundades i början av 1900-talet. Den förblev relativt isolerad fram till 1980-talet då investeringar i turism förvandlade den till en av världens ledande badorter. Trots moderniseringen har den gamla stadsdelen El Dahar behållit sin traditionella egyptiska charm med livliga souker och kaféer."
            },
            nature: {
                en: "Hurghada sits on the edge of the Red Sea, renowned for its spectacular coral reefs and diverse marine life, making it a global diving hotspot. To the west lies the vast Eastern Desert with its dramatic mountains and canyons. Offshore, the Giftun Islands offer pristine white sands and turquoise waters reminiscent of the Caribbean.",
                sv: "Hurghada ligger vid Röda havets kant, känt för sina spektakulära korallrev och varierande marina liv, vilket gör det till en global hotspot för dykning. Västerut ligger den vidsträckta östra öknen med sina dramatiska berg och kanjoner. Utanför kusten erbjuder Giftun-öarna orörd vit sand och turkost vatten som påminner om Karibien."
            }
        },
        gallery: [
            "https://images.unsplash.com/photo-1544521096-7c98c084d3b6?q=80&w=1200",
            "https://images.unsplash.com/photo-1615582382909-5b6d510255b3?q=80&w=1200",
            "https://images.unsplash.com/photo-1619052233980-0a2a51636139?q=80&w=1200"
        ],
        sections: {
            thingsToDo: [
                {
                    id: 'hurghada-diving',
                    title: { en: 'Red Sea Diving', sv: 'Röda Havets Dykning' },
                    description: { en: 'Explore world-famous coral reefs and shipwrecks with crystal-clear visibility.', sv: 'Utforska världsberömda korallrev och vrak med kristallklar sikt.' },
                    image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?q=80&w=800&auto=format&fit=crop',
                    category: 'adventure',
                    duration: 'Half day',
                    price: '€50-80'
                },
                {
                    id: 'hurghada-giftun',
                    title: { en: 'Giftun Island Trip', sv: 'Giftun Island Utflykt' },
                    description: { en: 'Visit the pristine beaches and snorkel in protected marine areas.', sv: 'Besök de orörda stränderna och snorkla i skyddade marinområden.' },
                    image: 'https://images.unsplash.com/photo-1519046904884-53103b34b206?q=80&w=800&auto=format&fit=crop',
                    category: 'beach',
                    duration: 'Full day',
                    price: '€35'
                },
                {
                    id: 'hurghada-desert',
                    title: { en: 'Desert Safari', sv: 'Ökensafari' },
                    description: { en: 'Quad biking, camel riding, and Bedouin dinner under the stars.', sv: 'Fyrhjuling, kamelritt och beduinmiddag under stjärnorna.' },
                    image: 'https://images.unsplash.com/photo-1547234935-80c7145ec969?q=80&w=800&auto=format&fit=crop',
                    category: 'adventure',
                    duration: 'Half day',
                    price: '€45'
                },
                {
                    id: 'hurghada-luxor',
                    title: { en: 'Luxor Day Trip', sv: 'Dagstur till Luxor' },
                    description: { en: 'Visit the Valley of the Kings, Karnak Temple, and ancient Egyptian treasures.', sv: 'Besök Konungarnas dal, Karnak-templet och forntida egyptiska skatter.' },
                    image: 'https://images.unsplash.com/photo-1539768942893-daf53e448371?q=80&w=800&auto=format&fit=crop',
                    category: 'culture',
                    duration: 'Full day',
                    price: '€100'
                }
            ],
            attractions: [
                {
                    id: 'attr-hurghada-aquarium',
                    title: { en: 'Hurghada Grand Aquarium', sv: 'Hurghada Stora Akvariet' },
                    description: { en: 'The Red Sea\'s marine life showcased in a modern aquarium complex.', sv: 'Röda havets marina liv visas i ett modernt akvariekomplex.' },
                    image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?q=80&w=800&auto=format&fit=crop',
                    category: 'family',
                    entryFee: '€25'
                },
                {
                    id: 'attr-hurghada-marina',
                    title: { en: 'Hurghada Marina', sv: 'Hurghada Marina' },
                    description: { en: 'Modern waterfront promenade with shops, restaurants, and nightlife.', sv: 'Modern strandpromenad med butiker, restauranger och nattliv.' },
                    image: 'https://images.unsplash.com/photo-1559339352-11d035aa65de?q=80&w=800&auto=format&fit=crop',
                    category: 'entertainment'
                }
            ],
            restaurants: [
                {
                    id: 'rest-hurghada-moby',
                    name: 'Moby Dick',
                    description: { en: 'Fresh seafood restaurant on the marina with views of yachts and Red Sea.', sv: 'Färsk fiskrestaurang vid marinan med utsikt över yachter och Röda havet.' },
                    image: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=800',
                    cuisine: 'Seafood',
                    priceRange: '$$$',
                    rating: 4.5
                }
            ],
            itineraries: [
                {
                    id: 'itin-hurghada-5day',
                    title: { en: '5 Days in Hurghada', sv: '5 Dagar i Hurghada' },
                    description: { en: 'Beach, diving, desert, and ancient history combined.', sv: 'Strand, dykning, öken och forntida historia kombinerat.' },
                    image: 'https://images.unsplash.com/photo-1544521096-7c98c084d3b6?q=80&w=800&auto=format&fit=crop',
                    days: 5,
                    highlights: {
                        en: ['Days 1-2: Beach & diving', 'Day 3: Giftun Island', 'Day 4: Desert safari', 'Day 5: Luxor trip'],
                        sv: ['Dag 1-2: Strand & dykning', 'Dag 3: Giftun Island', 'Dag 4: Ökensafari', 'Dag 5: Luxor-resa']
                    }
                }
            ],
            practicalInfo: {
                currency: { code: 'EGP', name: { en: 'Egyptian Pound', sv: 'Egyptiskt Pund' } },
                language: { en: 'Arabic (English spoken in resorts)', sv: 'Arabiska (engelska talas på resorts)' },
                timezone: 'GMT+2 (EET)',
                visaInfo: { en: 'Visa on arrival available for most nationalities (~$25 USD).', sv: 'Visum vid ankomst tillgängligt för de flesta nationaliteter (~$25 USD).' },
                bestTimeToVisit: { en: 'October-April for pleasant weather. May-September very hot but great for diving.', sv: 'Oktober-april för behagligt väder. Maj-september mycket varmt men bra för dykning.' },
                transport: [
                    { title: { en: 'Airport', sv: 'Flygplats' }, description: { en: 'Hurghada Airport (HRG) with hotel transfers widely available.', sv: 'Hurghada flygplats (HRG) med hotelltransfer allmänt tillgänglig.' }, icon: 'flight' }
                ],
                tips: {
                    en: ['Book diving trips in advance', 'Bring reef-safe sunscreen', 'Bargain in local shops', 'Stay hydrated'],
                    sv: ['Boka dykturer i förväg', 'Ta med revvänlig solkräm', 'Pruta i lokala butiker', 'Håll dig hydrerad']
                }
            }
        }
    },
    {
        id: '5',
        destinationId: '57',
        slug: 'dubai',
        title: "Dubai, UAE",
        titleSv: "Dubai, Förenade Arabemiraten",
        imageUrl: "https://images.pexels.com/photos/1534411/pexels-photo-1534411.jpeg?auto=compress&cs=tinysrgb&w=1600",
        price: "Fr. 8500 kr",
        type: "Luxury City",
        isActive: true,
        isFeatured: true,
        landingPage: {
            heroImage: "https://images.pexels.com/photos/1534411/pexels-photo-1534411.jpeg?auto=compress&cs=tinysrgb&w=1600",
            description: {
                en: "A city of superlatives where the future meets tradition. Experience world-class shopping, thrilling desert safaris, and breathtaking skyline views like nowhere else on Earth. From the iconic Burj Khalifa to the man-made Palm Jumeirah, Dubai is a testament to human ambition.",
                sv: "En stad av superlativ där framtiden möter traditionen. Upplev shopping i världsklass, spännande ökensafari och hisnande vyer över stadssilhuetten som ingen annanstans på jorden. Från den ikoniska Burj Khalifa till den konstgjorda Palm Jumeirah."
            },
            featuredHotels: [
                { hotelId: "325757", queryOffsetDays: 30, queryDuration: 5 }, // The Ritz-Carlton Dubai
                { hotelId: "325772", queryOffsetDays: 45, queryDuration: 5 }, // Grand Hyatt Dubai
                { hotelId: "326153", queryOffsetDays: 60, queryDuration: 7 }  // One&Only The Palm
            ]
        },
        richContent: {
            history: {
                en: "Dubai's transformation from a small fishing and pearl-diving village to a global metropolis is nothing short of miraculous. Establishing itself as a major trading hub in the early 20th century, the discovery of oil in 1966 accelerated its development. Today, it stands as a global hub for tourism, trade, and finance, retaining its Bedouin heritage in the heart of a futuristic city.",
                sv: "Dubais förvandling från en liten fiske- och pärldykarlandsby till en global metropol är inget annat än mirakulös. Etablerad som ett viktigt handelscentrum i början av 1900-talet, accelererade upptäckten av olja 1966 dess utveckling. Idag står den som ett globalt nav för turism, handel och finans, samtidigt som den behåller sitt beduinarv i hjärtat av en futuristisk stad."
            },
            nature: {
                en: "Beyond the skyscrapers lies the majestic Arabian Desert. The stark beauty of the golden dunes offers a serene escape from the city buzz. To the west, the turquoise waters of the Arabian Gulf provide pristine beaches and rich marine life, creating a unique ecosystem where desert meets ocean.",
                sv: "Bortom skyskraporna ligger den majestätiska arabiska öknen. De gyllene sanddynernas skönhet erbjuder en fridfull flykt från stadens brus. Västerut erbjuder Persiska vikens turkosa vatten orörda stränder och ett rikt marint liv, vilket skapar ett unikt ekosystem där öken möter hav."
            }
        },
        gallery: [
            "https://images.unsplash.com/photo-1512453979798-5ea90b792d50?q=80&w=1200",
            "https://images.unsplash.com/photo-1546412414-e1885259563a?q=80&w=1200",
            "https://images.unsplash.com/photo-1582672060674-bc2bd808a8b5?q=80&w=1200",
            "https://images.unsplash.com/photo-1578895122987-a57e363b307d?q=80&w=1200"
        ],
        sections: {
            thingsToDo: [
                {
                    id: 'dubai-burj',
                    title: { en: 'Burj Khalifa At The Top', sv: 'Burj Khalifa På Toppen' },
                    description: { en: 'Ascend the world\'s tallest building for panoramic views from 555m high.', sv: 'Bestig världens högsta byggnad för panoramavyer från 555 meters höjd.' },
                    image: 'https://images.unsplash.com/photo-1512453979798-5ea90b792d50?q=80&w=800&auto=format&fit=crop',
                    category: 'culture',
                    duration: '2-3 hours',
                    price: 'AED 149+'
                },
                {
                    id: 'dubai-desert',
                    title: { en: 'Desert Safari Adventure', sv: 'Ökensafari Äventyr' },
                    description: { en: 'Dune bashing, camel riding, BBQ dinner, and entertainment under the stars.', sv: 'Dynkörning, kamelritt, BBQ-middag och underhållning under stjärnorna.' },
                    image: 'https://images.unsplash.com/photo-1547234935-80c7145ec969?q=80&w=800&auto=format&fit=crop',
                    category: 'adventure',
                    duration: '6-7 hours',
                    price: 'AED 200+'
                },
                {
                    id: 'dubai-mall',
                    title: { en: 'Dubai Mall & Fountains', sv: 'Dubai Mall & Fontänerna' },
                    description: { en: 'World\'s largest mall with aquarium, ice rink, and choreographed fountain show.', sv: 'Världens största köpcentrum med akvarium, skridskobana och koreograferad fontänshow.' },
                    image: 'https://images.unsplash.com/photo-1580674684081-7617fbf3d745?q=80&w=800&auto=format&fit=crop',
                    category: 'culture',
                    duration: '3-4 hours',
                    price: 'Free entry'
                },
                {
                    id: 'dubai-creek',
                    title: { en: 'Old Dubai & Gold Souk', sv: 'Gamla Dubai & Guldbasaren' },
                    description: { en: 'Explore traditional souks, abra boat rides across the Creek, and spice markets.', sv: 'Utforska traditionella basarer, abra-båtturer över Creek och kryddmarknader.' },
                    image: 'https://images.unsplash.com/photo-1578895122987-a57e363b307d?q=80&w=800&auto=format&fit=crop',
                    category: 'culture',
                    duration: '3-4 hours',
                    price: 'Free'
                },
                {
                    id: 'dubai-palm',
                    title: { en: 'Palm Jumeirah Experience', sv: 'Palm Jumeirah Upplevelse' },
                    description: { en: 'Visit the iconic man-made island with Atlantis water park and beach clubs.', sv: 'Besök den ikoniska konstgjorda ön med Atlantis vattenpark och strandklubbar.' },
                    image: 'https://images.unsplash.com/photo-1582672060674-bc2bd808a8b5?q=80&w=800&auto=format&fit=crop',
                    category: 'beach',
                    duration: 'Full day',
                    price: 'Varies'
                }
            ],
            attractions: [
                {
                    id: 'attr-dubai-frame',
                    title: { en: 'Dubai Frame', sv: 'Dubai Frame' },
                    description: { en: 'The world\'s largest picture frame offering views of old and new Dubai.', sv: 'Världens största tavelram med utsikt över gamla och nya Dubai.' },
                    image: 'https://images.unsplash.com/photo-1597659840241-37e2b9c2f55f?q=80&w=800&auto=format&fit=crop',
                    category: 'landmark',
                    entryFee: 'AED 50'
                },
                {
                    id: 'attr-dubai-marina',
                    title: { en: 'Dubai Marina', sv: 'Dubai Marina' },
                    description: { en: 'Stunning waterfront with skyscrapers, yacht clubs, and dining.', sv: 'Fantastisk strandpromenad med skyskrapor, yachtklubbar och restauranger.' },
                    image: 'https://images.unsplash.com/photo-1559339352-11d035aa65de?q=80&w=800&auto=format&fit=crop',
                    category: 'scenic'
                },
                {
                    id: 'attr-dubai-museum',
                    title: { en: 'Museum of the Future', sv: 'Framtidens Museum' },
                    description: { en: 'Award-winning architectural marvel exploring technology and innovation.', sv: 'Prisbelönt arkitektoniskt mästerverk som utforskar teknik och innovation.' },
                    image: 'https://images.unsplash.com/photo-1597659840241-37e2b9c2f55f?q=80&w=800&auto=format&fit=crop',
                    category: 'culture',
                    entryFee: 'AED 149'
                }
            ],
            restaurants: [
                {
                    id: 'rest-dubai-atmosphere',
                    name: 'At.mosphere',
                    description: { en: 'World\'s highest restaurant in Burj Khalifa with stunning views.', sv: 'Världens högsta restaurang i Burj Khalifa med fantastisk utsikt.' },
                    image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=800',
                    cuisine: 'International Fine Dining',
                    priceRange: '$$$$',
                    rating: 4.7
                },
                {
                    id: 'rest-dubai-pierchic',
                    name: 'Pierchic',
                    description: { en: 'Seafood restaurant on a pier with Arabian Gulf views.', sv: 'Fiskrestaurang på en pir med utsikt över Arabiska viken.' },
                    image: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=800',
                    cuisine: 'Seafood',
                    priceRange: '$$$$',
                    rating: 4.8
                },
                {
                    id: 'rest-dubai-al-fanar',
                    name: 'Al Fanar Restaurant',
                    description: { en: 'Authentic Emirati cuisine in a traditional 1960s setting.', sv: 'Autentiskt emiratiskt kök i en traditionell 1960-talsmiljö.' },
                    image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=800',
                    cuisine: 'Emirati',
                    priceRange: '$$$',
                    rating: 4.6
                }
            ],
            itineraries: [
                {
                    id: 'itin-dubai-3day',
                    title: { en: '3 Days in Dubai', sv: '3 Dagar i Dubai' },
                    description: { en: 'Experience the best of modern and traditional Dubai.', sv: 'Upplev det bästa av modernt och traditionellt Dubai.' },
                    image: 'https://images.unsplash.com/photo-1512453979798-5ea90b792d50?q=80&w=800&auto=format&fit=crop',
                    days: 3,
                    highlights: {
                        en: ['Day 1: Burj Khalifa & Dubai Mall', 'Day 2: Old Dubai & Desert Safari', 'Day 3: Palm Jumeirah & Beach'],
                        sv: ['Dag 1: Burj Khalifa & Dubai Mall', 'Dag 2: Gamla Dubai & Ökensafari', 'Dag 3: Palm Jumeirah & Strand']
                    }
                },
                {
                    id: 'itin-dubai-5day',
                    title: { en: '5 Days in Dubai', sv: '5 Dagar i Dubai' },
                    description: { en: 'Complete luxury experience with Abu Dhabi day trip.', sv: 'Komplett lyxupplevelse med dagstur till Abu Dhabi.' },
                    image: 'https://images.unsplash.com/photo-1546412414-e1885259563a?q=80&w=800&auto=format&fit=crop',
                    days: 5,
                    highlights: {
                        en: ['Days 1-2: Dubai highlights', 'Day 3: Desert adventure', 'Day 4: Abu Dhabi trip', 'Day 5: Shopping & spa'],
                        sv: ['Dag 1-2: Dubais höjdpunkter', 'Dag 3: Ökenäventyr', 'Dag 4: Abu Dhabi-tur', 'Dag 5: Shopping & spa']
                    }
                }
            ],
            practicalInfo: {
                currency: { code: 'AED', name: { en: 'UAE Dirham', sv: 'UAE Dirham' } },
                language: { en: 'Arabic (English universally spoken)', sv: 'Arabiska (engelska talas överr allt)' },
                timezone: 'GMT+4 (GST)',
                visaInfo: { en: 'Visa on arrival or pre-approved e-visa for most nationalities.', sv: 'Visum vid ankomst eller förhandsgodkänt e-visum för de flesta nationaliteter.' },
                bestTimeToVisit: { en: 'November-March for pleasant outdoor weather. Summer is very hot but great for indoor attractions.', sv: 'November-mars för behagligt utomhusväder. Sommaren är mycket varm men bra för inomhusaktiviteter.' },
                transport: [
                    { title: { en: 'Airport', sv: 'Flygplats' }, description: { en: 'Dubai International (DXB) with metro connection to the city.', sv: 'Dubai International (DXB) med metroanslutning till staden.' }, icon: 'flight' },
                    { title: { en: 'Metro', sv: 'Tunnelbana' }, description: { en: 'Modern, cheap metro covers main attractions. Buy Nol card.', sv: 'Modern, billig tunnelbana täcker huvudattraktioner. Köp Nol-kort.' }, icon: 'subway' },
                    { title: { en: 'Taxi', sv: 'Taxi' }, description: { en: 'Affordable taxis with meters. Use Uber and Careem apps.', sv: 'Prisvärda taxibilar med taxameter. Använd Uber och Careem-appar.' }, icon: 'local_taxi' }
                ],
                tips: {
                    en: ['Dress modestly outside resorts', 'Book Burj Khalifa tickets online', 'Friday brunch is a Dubai tradition', 'Avoid outdoor activities in summer midday'],
                    sv: ['Klä dig blygsamt utanför resorts', 'Boka Burj Khalifa-biljetter online', 'Fredagsbrunch är en Dubai-tradition', 'Undvik utomhusaktiviteter på sommardagar']
                }
            }
        }
    },
    {
        id: '6',
        destinationId: '76',
        slug: 'mallorca',
        title: "Mallorca, Spain",
        titleSv: "Mallorca, Spanien",
        imageUrl: "https://images.pexels.com/photos/3889704/pexels-photo-3889704.jpeg?auto=compress&cs=tinysrgb&w=1600",
        price: "Fr. 4900 kr",
        type: "Flight + Hotel",
        isActive: true,
        isFeatured: true,
        landingPage: {
            heroImage: "https://images.pexels.com/photos/3889704/pexels-photo-3889704.jpeg?auto=compress&cs=tinysrgb&w=1600",
            description: {
                en: "The jewel of the Balearics. From stunning coves and limestone mountains to the vibrant capital city of Palma, Mallorca offers something for everyone.",
                sv: "Juvelen i Balearerna. Från fantastiska vikar och kalkstensberg till den pulserande huvudstaden Palma, Mallorca erbjuder något för alla."
            },
            featuredHotels: []
        },
        richContent: {
            history: {
                en: "Mallorca has a rich past marked by Romans, Moors, and the Christian reconquest. The iconic La Seu Cathedral in Palma, built on the site of a mosque, dominates the skyline. The island is dotted with historic 'possessiós' (manor houses), ancient Talaiotic settlements, and charming stone villages like Valldemossa.",
                sv: "Mallorca har ett rikt förflutet präglat av romare, morer och den kristna återerövringen. Den ikoniska La Seu-katedralen i Palma, byggd på platsen för en moské, dominerar stadsbilden. Ön är beströdd med historiska herrgårdar, antika talaiotiska bosättningar och charmiga stenbyar som Valldemossa."
            },
            nature: {
                en: "The landscape is defined by the dramatic Serra de Tramuntana mountains in the west, a UNESCO World Heritage site. The coastline is famous for its 'calas'—small, secluded coves with crystal clear water. Inland, you'll find olive groves, almond orchards, and the spectacular Caves of Drach with their underground lakes.",
                sv: "Landskapet definieras av de dramatiska bergen Serra de Tramuntana i väster, ett av UNESCO:s världsarv. Kusten är känd för sina 'calas' – små, avskilda vikar med kristallklart vatten. Inåt landet hittar du olivlundar, mandelodlingar och de spektakulära Drach-grottorna med sina underjordiska sjöar."
            }
        },
        sections: {
            thingsToDo: [
                {
                    id: 'mallorca-palma',
                    title: { en: 'Explore Palma Old Town', sv: 'Utforska Palmas Gamla Stan' },
                    description: { en: 'Visit the stunning La Seu Cathedral, royal palace, and atmospheric old quarters.', sv: 'Besök den fantastiska La Seu-katedralen, kungliga palatset och atmosfäriska gamla kvarteren.' },
                    image: 'https://images.pexels.com/photos/1034662/pexels-photo-1034662.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
                    category: 'culture',
                    duration: 'Half day',
                    price: 'Free'
                },
                {
                    id: 'mallorca-tramuntana',
                    title: { en: 'Serra de Tramuntana Drive', sv: 'Serra de Tramuntana Biltur' },
                    description: { en: 'Scenic mountain drive through UNESCO World Heritage landscapes to Valldemossa and Sóller.', sv: 'Naturskönt bergskörning genom UNESCO-landskap till Valldemossa och Sóller.' },
                    image: 'https://images.pexels.com/photos/1034662/pexels-photo-1034662.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
                    category: 'nature',
                    duration: 'Full day',
                    price: 'Free'
                },
                {
                    id: 'mallorca-cala',
                    title: { en: 'Beach Hopping Calas', sv: 'Strandhopping Calas' },
                    description: { en: 'Discover hidden coves with turquoise water like Cala Mondragó and Cala Varques.', sv: 'Upptäck gömda vikar med turkost vatten som Cala Mondragó och Cala Varques.' },
                    image: 'https://images.pexels.com/photos/1034662/pexels-photo-1034662.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
                    category: 'beach',
                    duration: 'Full day',
                    price: 'Free'
                },
                {
                    id: 'mallorca-caves',
                    title: { en: 'Caves of Drach', sv: 'Drach-grottorna' },
                    description: { en: 'Explore spectacular underground caves with one of the world\'s largest subterranean lakes.', sv: 'Utforska spektakulära underjordiska grottor med en av världens största underjordiska sjöar.' },
                    image: 'https://images.pexels.com/photos/1034662/pexels-photo-1034662.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
                    category: 'nature',
                    duration: '2 hours',
                    price: '€17'
                },
                {
                    id: 'mallorca-train',
                    title: { en: 'Sóller Vintage Train', sv: 'Sóller Veterantåg' },
                    description: { en: 'Ride the historic wooden train from Palma through mountains to the charming Sóller valley.', sv: 'Åk med det historiska trätåget från Palma genom bergen till den charmiga Sóller-dalen.' },
                    image: 'https://images.pexels.com/photos/1034662/pexels-photo-1034662.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
                    category: 'culture',
                    duration: '4 hours',
                    price: '€25'
                }
            ],
            attractions: [
                {
                    id: 'attr-mallorca-cathedral',
                    title: { en: 'La Seu Cathedral', sv: 'La Seu-katedralen' },
                    description: { en: 'Stunning Gothic cathedral with Gaudí-designed interior and sea views.', sv: 'Fantastisk gotisk katedral med Gaudí-designad interiör och havsutsikt.' },
                    image: 'https://images.pexels.com/photos/1034662/pexels-photo-1034662.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
                    category: 'historic',
                    entryFee: '€10'
                },
                {
                    id: 'attr-mallorca-valldemossa',
                    title: { en: 'Valldemossa', sv: 'Valldemossa' },
                    description: { en: 'Charming mountain village where Chopin stayed, with a historic monastery.', sv: 'Charmig bergsby där Chopin bodde, med ett historiskt kloster.' },
                    image: 'https://images.pexels.com/photos/1034662/pexels-photo-1034662.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
                    category: 'scenic'
                },
                {
                    id: 'attr-mallorca-cap',
                    title: { en: 'Cap de Formentor', sv: 'Cap de Formentor' },
                    description: { en: 'Dramatic cliffs and lighthouse at Mallorca\'s northernmost point.', sv: 'Dramatiska klippor och fyr vid Mallorcas nordligaste punkt.' },
                    image: 'https://images.pexels.com/photos/1034662/pexels-photo-1034662.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
                    category: 'nature'
                }
            ],
            restaurants: [
                {
                    id: 'rest-mallorca-marc',
                    name: 'At.mosphere',
                    description: { en: 'World\'s highest restaurant in Burj Khalifa with stunning views.', sv: 'Världens högsta restaurang i Burj Khalifa med fantastisk utsikt.' },
                    image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=800',
                    cuisine: 'Mediterranean Fine Dining',
                    priceRange: '$$$$',
                    rating: 4.8
                },
                {
                    id: 'rest-mallorca-simply',
                    name: 'Simply Fosh',
                    description: { en: 'Casual fine dining from Marc Fosh with creative tasting menus.', sv: 'Avslappnad finrestaurang från Marc Fosh med kreativa avsmakningsmenyer.' },
                    image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=800',
                    cuisine: 'Modern European',
                    priceRange: '$$$',
                    rating: 4.7
                },
                {
                    id: 'rest-mallorca-ca-na-toneta',
                    name: 'Ca Na Toneta',
                    description: { en: 'Farm-to-table Mallorcan cuisine in Caimari with local ingredients.', sv: 'Gård-till-bord mallorquinskt kök i Caimari med lokala råvaror.' },
                    image: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=800',
                    cuisine: 'Mallorcan',
                    priceRange: '$$$',
                    rating: 4.9
                }
            ],
            itineraries: [
                {
                    id: 'itin-mallorca-3day',
                    title: { en: '3 Days in Mallorca', sv: '3 Dagar på Mallorca' },
                    description: { en: 'City, mountains, and beaches in the perfect introduction.', sv: 'Stad, berg och stränder i den perfekta introduktionen.' },
                    image: "https://images.pexels.com/photos/3214958/pexels-photo-3214958.jpeg?auto=compress&cs=tinysrgb&w=1600",
                    days: 3,
                    highlights: {
                        en: ['Day 1: Palma Old Town & Cathedral', 'Day 2: Tramuntana & Valldemossa', 'Day 3: Beach cala hopping'],
                        sv: ['Dag 1: Palmas Gamla Stan & Katedralen', 'Dag 2: Tramuntana & Valldemossa', 'Dag 3: Strandvikshopping']
                    }
                },
                {
                    id: 'itin-mallorca-7day',
                    title: { en: '7 Days in Mallorca', sv: '7 Dagar på Mallorca' },
                    description: { en: 'Complete island exploration with time to relax.', sv: 'Komplett öutforskning med tid att koppla av.' },
                    image: "https://images.pexels.com/photos/3214958/pexels-photo-3214958.jpeg?auto=compress&cs=tinysrgb&w=1600",
                    days: 7,
                    highlights: {
                        en: ['Days 1-2: Palma city exploration', 'Days 3-4: Tramuntana adventures', 'Day 5: Caves & East coast', 'Days 6-7: Beach relaxation'],
                        sv: ['Dag 1-2: Utforska staden Palma', 'Dag 3: Ökenäventyr', 'Dag 4: Abu Dhabi-tur', 'Dag 5: Shopping & spa']
                    }
                }
            ],
            practicalInfo: {
                currency: { code: 'EUR', name: { en: 'Euro', sv: 'Euro' } },
                language: { en: 'Spanish & Catalan (English widely spoken)', sv: 'Spanska & Katalanska (engelska talas allmänt)' },
                timezone: 'GMT+1 (CET)',
                visaInfo: { en: 'EU citizens: No visa. Others: Schengen visa.', sv: 'EU-medborgare: Inget visum. Övriga: Schengen-visum.' },
                bestTimeToVisit: { en: 'May-October for beach weather. March-April for almond blossoms.', sv: 'Maj-oktober för strandväder. Mars-april för mandel blommor.' },
                transport: [
                    { title: { en: 'Airport', sv: 'Flygplats' }, description: { en: 'Palma de Mallorca Airport (PMI) is 8km from Palma. Buses and taxis available.', sv: 'Palma de Mallorca flygplats (PMI) ligger 8 km från Palma. Bussar och taxi finns.' }, icon: 'flight' },
                    { title: { en: 'Car Rental', sv: 'Hyrbil' }, description: { en: 'Essential for exploring the island. Book in advance for summer.', sv: 'Nödvändigt för att utforska ön. Boka i förväg för sommaren.' }, icon: 'directions_car' },
                    { title: { en: 'Vintage Train', sv: 'Veterantåg' }, description: { en: 'Historic Sóller train offers a scenic alternative to driving.', sv: 'Historiska Sóller-tåget erbjuder ett naturskönt alternativ till bilkörning.' }, icon: 'train' }
                ],
                tips: {
                    en: ['Book restaurants in advance', 'Rent a car for hidden calas', 'Visit calas early to avoid crowds', 'Try ensaimada pastry and sobrasada'],
                    sv: ['Boka restauranger i förväg', 'Hyr bil för gömda vikar', 'Besök vikar tidigt för att undvika folkmassor', 'Prova ensaimada-bakverk och sobrasada']
                }
            }
        }
    },

    // Egypt - New Addition
    {
        id: '7',
        destinationId: '24', // Egypt from INITIAL_DESTINATION_MAPPINGS
        slug: 'egypt',
        title: "Egypt",
        titleSv: "Egypten",
        imageUrl: "https://images.unsplash.com/photo-1539768942893-daf53e448371?q=80&w=2000&auto=format&fit=crop",
        price: "Fr. 5200 kr",
        type: "History + Beach",
        isActive: true,
        isFeatured: true,
        landingPage: {
            heroImage: "https://images.unsplash.com/photo-1539768942893-daf53e448371?q=80&w=2000&auto=format&fit=crop",
            description: {
                en: "Experience the land of Pharaohs. Egypt offers world-famous ancient wonders, stunning Red Sea beaches, and vibrant culture. From the pyramids to Hurghada's resorts.",
                sv: "Upplev faraoernas land. Egypten erbjuder världsberömda antika underverk, fantastiska stränder vid Röda havet och levande kultur. Från pyramiderna till Hurghadas resorter."
            },
            featuredHotels: []
        },
        richContent: {
            history: {
                en: "Home to one of the world's oldest civilizations, Egypt's monuments have stood for over 4,500 years. The Great Pyramids of Giza, the Sphinx, Luxor's temples, and the Valley of the Kings are just some of the legendary sites awaiting you.",
                sv: "Hem för en av världens äldsta civilisationer, Egyptens monument har stått i över 4 500 år. De stora pyramiderna i Giza, Sfinxen, Luxors tempel och Konungarnas dal är bara några av de legendariska platserna som väntar."
            },
            nature: {
                en: "Beyond ancient history, Egypt offers the Red Sea Riviera with world-class diving and snorkeling in coral reefs teeming with marine life. Hurghada and Sharm El Sheikh are perfect for beach lovers.",
                sv: "Bortom antik historia erbjuder Egypten Röda havets riviera med dygning och snorkling i världsklass i korallrev fyllda med marint liv. Hurghada och Sharm El Sheikh är perfekta för strandälskare."
            }
        },
        gallery: [
            "https://images.unsplash.com/photo-1539768942893-daf53e448371?q=80&w=1200",
            "https://images.unsplash.com/photo-1568322445389-f64ac2515020?q=80&w=1200"
        ]
    },
    // Thailand - New Addition
    {
        id: '8',
        destinationId: '109', // Thailand
        slug: 'thailand',
        title: "Thailand",
        titleSv: "Thailand",
        imageUrl: "https://images.pexels.com/photos/3601425/pexels-photo-3601425.jpeg?auto=compress&cs=tinysrgb&w=1600",
        price: "Fr. 6500 kr",
        type: "Exotic",
        isActive: true,
        isFeatured: true,
        landingPage: {
            heroImage: "https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?q=80&w=2000&auto=format&fit=crop",
            description: {
                en: "The Land of Smiles invokes images of ornate temples, tropical beaches, and Thai culinary delights. From the bustle of Bangkok to the serenity of the islands.",
                sv: "Leendets land framkallar bilder av utsmyckade tempel, tropiska stränder och thailändska kulinariska läckerheter. Från vimlet i Bangkok till lugnet på öarna."
            },
            featuredHotels: []
        },
        richContent: {
            history: {
                en: "Thailand is the only Southeast Asian nation never colonized by a European power. Its history is divided into kingdoms like Sukhothai, Ayutthaya, and Thonburi. The country is known for its devout Buddhist traditions and royal heritage.",
                sv: "Thailand är den enda sydostasiatiska nationen som aldrig har koloniserats av en europeisk makt. Dess historia är uppdelad i kungadömen som Sukhothai, Ayutthaya och Thonburi. Landet är känt för sina fromma buddhistiska traditioner och kungliga arv."
            },
            nature: {
                en: "Thailand's geography is diverse, ranging from the mountainous north (Chiang Mai) to the limestone islands of the Andaman Sea. Dozens of national parks protect rainforests, waterfalls, and marine life.",
                sv: "Thailands geografi är varierad, allt från det bergiga norra (Chiang Mai) till kalkstensöarna i Andamansjön. Dussintals nationalparker skyddar regnskogar, vattenfall och marint liv."
            }
        },
        gallery: [
            "https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?q=80&w=1200",
            "https://images.unsplash.com/photo-1506665531195-3566af2b4dfa?q=80&w=1200"
        ],
        sections: {
            thingsToDo: [
                {
                    id: 'thai-phi-phi',
                    title: { en: 'Phi Phi Islands', sv: 'Phi Phi-öarna' },
                    description: { en: 'Visit the world-famous Maya Bay and snorkel in turquoise waters.', sv: 'Besök världsberömda Maya Bay och snorkla i turkost vatten.' },
                    image: 'https://images.unsplash.com/photo-1537956965359-7573183d1f57?q=80&w=800&auto=format&fit=crop',
                    category: 'nature',
                    duration: 'Full day',
                    price: '€60'
                }
            ],
            attractions: [
                {
                    id: 'attr-thai-grand-palace',
                    title: { en: 'Grand Palace', sv: 'Grand Palace' },
                    description: { en: 'The dazzling, spectacular former residence of the Kings of Siam in Bangkok.', sv: 'Det bländande, spektakulära tidigare residenset för kungarna av Siam i Bangkok.' },
                    image: 'https://images.unsplash.com/photo-1599525281489-0b701250269f?q=80&w=800&auto=format&fit=crop',
                    category: 'historic'
                }
            ],
            restaurants: [],
            itineraries: [],
            practicalInfo: {
                currency: { code: 'THB', name: { en: 'Thai Baht', sv: 'Thailändska Baht' } },
                language: { en: 'Thai (English widely spoken)', sv: 'Thailändska (engelska talas allmänt)' },
                timezone: 'GMT+7',
                visaInfo: { en: 'Visa exemption for many countries (30-45 days).', sv: 'Visumundantag för många länder (30-45 dagar).' },
                bestTimeToVisit: { en: 'November to February (Cool season).', sv: 'November till februari (Svala perioden).' },
                transport: [],
                tips: { en: [], sv: [] }
            }
        }
    }
];

// --- Database Connectivity Check ---

export const checkDbConnection = (): boolean => {
    try {
        const testKey = '__db_connectivity_test__';
        localStorage.setItem(testKey, '1');
        const val = localStorage.getItem(testKey);
        localStorage.removeItem(testKey);
        return val === '1';
    } catch (e) {
        console.error("Database (LocalStorage) connection failed:", e);
        return false;
    }
};

export const getSystemHealth = (logs: any[]): SystemHealth => {
    const db = checkDbConnection();
    const errors = logs.filter(l => l.status === 'error').length;
    const lastApi = logs.length > 0 ? logs[0].status : 'Unknown';
    return {
        dbStatus: db,
        apiStatus: lastApi,
        errorCount: errors
    };
};

// --- CMS Initialization (Hybrid: Seed to MongoDB via API) ---

export const initializeCMS = async () => {
    try {
        // Check if already seeded (use a simple flag in localStorage to avoid re-seeding)
        const isSeeded = localStorage.getItem('hybrid_cms_seeded');
        if (isSeeded === 'true') {
            console.log('[HYBRID] CMS already initialized, skipping seed.');
            return;
        }

        console.log('[HYBRID] Initializing CMS — seeding preloaded data to MongoDB...');

        // Seed PRELOADED_HOTELS to MongoDB via API
        const hotelResults = await Promise.allSettled(
            PRELOADED_HOTELS.map(hotel =>
                fetch(`/api/admin/hotels/${hotel.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ...hotel, dataSource: 'seed', isEdited: true })
                })
            )
        );
        const hotelsFailed = hotelResults.filter(r => r.status === 'rejected').length;
        console.log(`[HYBRID] Seeded ${PRELOADED_HOTELS.length - hotelsFailed}/${PRELOADED_HOTELS.length} hotels to MongoDB`);

        // Seed INITIAL_DESTINATIONS to MongoDB via API
        try {
            const seedRes = await fetch('/api/destinations/seed-preloaded', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });
            const seedData = await seedRes.json();
            console.log('[HYBRID] Destination seed result:', seedData.message || seedData);
        } catch (e) {
            console.warn('[HYBRID] Destination seed failed (server may be offline):', e);
        }

        // Ensure Whitelist Exists (keep in localStorage for now — widget config)
        if (!localStorage.getItem(STORAGE_KEYS.HOTEL_WHITELIST)) {
            localStorage.setItem(STORAGE_KEYS.HOTEL_WHITELIST, JSON.stringify(INITIAL_WHITELIST));
        }

        // Mark as seeded
        localStorage.setItem('hybrid_cms_seeded', 'true');
        console.log('[HYBRID] CMS initialization complete.');

    } catch (e) {
        console.error('[HYBRID] CMS Initialization failed:', e);
    }
};

// --- Image Caching Services ---

export const getCachedImageMap = (): Record<string, string> | null => {
    try {
        const raw = localStorage.getItem(STORAGE_KEYS.DESTINATION_IMAGE_CACHE);
        if (!raw) return null;
        const cache: ImageCache = JSON.parse(raw);
        const now = Date.now();
        if (now - cache.timestamp > 86400000) {
            localStorage.removeItem(STORAGE_KEYS.DESTINATION_IMAGE_CACHE);
            return null;
        }
        return cache.images;
    } catch (e) { return null; }
};

export const saveCachedImageMap = (images: Record<string, string>) => {
    const cache: ImageCache = {
        timestamp: Date.now(),
        images
    };
    localStorage.setItem(STORAGE_KEYS.DESTINATION_IMAGE_CACHE, JSON.stringify(cache));
};

// --- Booking Services (Hybrid: API-backed) ---

export const getBookings = async (): Promise<BookingRecord[]> => {
    try {
        const res = await fetch('/api/bookings?limit=50');
        const data = await res.json();
        if (data.success && data.bookings) {
            return data.bookings;
        }
        return [];
    } catch (e) {
        console.error('[HYBRID] Failed to fetch bookings:', e);
        return [];
    }
};

export const saveBooking = async (booking: BookingRecord): Promise<void> => {
    try {
        await fetch('/api/bookings', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(booking)
        });
        triggerUpdateEvent();
    } catch (e) {
        console.error('[HYBRID] Failed to save booking:', e);
    }
};

export const updateBookingStatus = async (id: string, status: 'Confirmed' | 'Pending' | 'Failed' | 'Cancelled'): Promise<void> => {
    try {
        await fetch(`/api/bookings/${id}/status`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status })
        });
        triggerUpdateEvent();
    } catch (e) {
        console.error('[HYBRID] Failed to update booking status:', e);
    }
};

export const deleteBooking = async (id: string): Promise<void> => {
    try {
        await fetch(`/api/bookings/${id}`, { method: 'DELETE' });
        triggerUpdateEvent();
    } catch (e) {
        console.error('[HYBRID] Failed to delete booking:', e);
    }
};

// --- CMS Services (Destinations — Hybrid: API-First, MongoDB-backed) ---

export const getDestinations = async (): Promise<DestinationContent[]> => {
    try {
        const res = await fetch('/api/destinations');
        const data = await res.json();
        if (data.success && data.destinations) {
            return data.destinations;
        }
        // Fallback to INITIAL_DESTINATIONS if API returns empty
        console.warn('[HYBRID] API returned no destinations, using INITIAL_DESTINATIONS fallback');
        return INITIAL_DESTINATIONS;
    } catch (e) {
        console.error('[HYBRID] getDestinations API error, returning INITIAL_DESTINATIONS:', e);
        return INITIAL_DESTINATIONS;
    }
};

export const saveDestination = async (destination: DestinationContent): Promise<void> => {
    try {
        const res = await fetch(`/api/destinations/${destination.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(destination)
        });
        const data = await res.json();
        if (data.success) {
            triggerUpdateEvent();
        }
    } catch (e) {
        console.error('[HYBRID] Failed to save destination:', e);
    }
};

export const deleteDestination = async (id: string): Promise<void> => {
    try {
        const res = await fetch(`/api/destinations/${id}`, {
            method: 'DELETE'
        });
        const data = await res.json();
        if (data.success) {
            triggerUpdateEvent();
        }
    } catch (e) {
        console.error('[HYBRID] Failed to delete destination:', e);
    }
};

export const saveDestinations = async (destinations: DestinationContent[]): Promise<void> => {
    try {
        // Batch save via individual PUT calls
        const results = await Promise.allSettled(
            destinations.map(dest =>
                fetch(`/api/destinations/${dest.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(dest)
                })
            )
        );
        const failed = results.filter(r => r.status === 'rejected').length;
        if (failed > 0) console.warn(`[HYBRID] ${failed}/${destinations.length} destinations failed to save`);
        triggerUpdateEvent();
    } catch (e) {
        console.error('[HYBRID] Failed to save destinations:', e);
    }
};

// --- Destination ID Mappings Service (Hybrid: API-backed) ---

const ADMIN_CONFIG_API = '/api/admin/config';

export const getDestinationMappings = async (): Promise<DestinationMapping[]> => {
    try {
        const res = await fetch(`${ADMIN_CONFIG_API}/destination_mappings`);
        const data = await res.json();
        if (data.success && data.value) {
            return data.value;
        }
        return INITIAL_DESTINATION_MAPPINGS;
    } catch (e) {
        return INITIAL_DESTINATION_MAPPINGS;
    }
};

export const saveDestinationMapping = async (mapping: DestinationMapping): Promise<void> => {
    try {
        const mappings = await getDestinationMappings();
        const index = mappings.findIndex(m => m.name.toLowerCase() === mapping.name.toLowerCase());
        if (index !== -1) {
            mappings[index] = mapping;
        } else {
            mappings.push(mapping);
        }
        await fetch(`${ADMIN_CONFIG_API}/destination_mappings`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ value: mappings })
        });
        triggerUpdateEvent();
    } catch (e) {
        console.error('[HYBRID] Failed to save mapping:', e);
    }
};

export const deleteDestinationMapping = async (name: string): Promise<void> => {
    try {
        const mappings = (await getDestinationMappings()).filter(m => m.name !== name);
        await fetch(`${ADMIN_CONFIG_API}/destination_mappings`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ value: mappings })
        });
        triggerUpdateEvent();
    } catch (e) {
        console.error('[HYBRID] Failed to delete mapping:', e);
    }
};

export const lookupDestinationId = async (name: string): Promise<string | null> => {
    const mappings = await getDestinationMappings();
    const normalized = name.toLowerCase().trim();
    const found = mappings.find(m => m.name.toLowerCase() === normalized);
    return found ? found.id : null;
};

// --- Whitelist Service (Hybrid: API-backed) ---

export const getWhitelist = async (): Promise<WhitelistItem[]> => {
    try {
        const res = await fetch(`${ADMIN_CONFIG_API}/hotel_whitelist`);
        const data = await res.json();
        if (data.success && data.value) {
            return data.value;
        }
        return INITIAL_WHITELIST;
    } catch (e) {
        return INITIAL_WHITELIST;
    }
};

export const saveWhitelist = async (items: WhitelistItem[]): Promise<void> => {
    try {
        await fetch(`${ADMIN_CONFIG_API}/hotel_whitelist`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ value: items })
        });
        triggerUpdateEvent();
    } catch (e) {
        console.error('[HYBRID] Failed to save whitelist:', e);
    }
};

export const addToWhitelist = async (item: WhitelistItem): Promise<void> => {
    const list = await getWhitelist();
    if (!list.some(i => i.id === item.id)) {
        list.push(item);
        await saveWhitelist(list);
    }
};

export const removeFromWhitelist = async (id: string): Promise<void> => {
    const list = (await getWhitelist()).filter(i => i.id !== id);
    await saveWhitelist(list);
};

export const bulkImportWhitelist = async (text: string): Promise<number> => {
    const lines = text.split('\n');
    const list = await getWhitelist();
    let addedCount = 0;

    // Regex to match PXM123456 and Name
    const regex = /PXM(\d+)\s+(.*?)(\s+Hotel|\s+4STAR|\s+5\s+|$)/i;

    lines.forEach(line => {
        const match = line.match(regex);
        if (match && match[1] && match[2]) {
            const id = match[1];
            const name = match[2].trim();
            if (!list.some(i => i.id === id)) {
                list.push({ id, name });
                addedCount++;
            }
        }
    });

    if (addedCount > 0) {
        await saveWhitelist(list);
    }
    return addedCount;
};


// --- Ad & CTA Services (Unified: Backend-backed) ---

export const getHomepageAds = async (): Promise<Ad[]> => {
    try {
        const res = await fetch('/api/ads/public/homepage');
        const data = await res.json();
        return data.success ? data.ads : [];
    } catch (e) {
        return [];
    }
};

export const getSearchResultsAds = async (destinationId?: string, departureCity?: string): Promise<Ad[]> => {
    try {
        const params = new URLSearchParams();
        if (destinationId) params.append('destinationId', destinationId);
        if (departureCity) params.append('departureCity', departureCity);
        const res = await fetch(`/api/ads/public/search-results?${params.toString()}`);
        const data = await res.json();
        return data.success ? data.ads : [];
    } catch (e) {
        return [];
    }
};

export const getDestinationAds = async (destinationId: string): Promise<Ad[]> => {
    try {
        const res = await fetch(`/api/ads/public/destination/${destinationId}`);
        const data = await res.json();
        return data.success ? data.ads : [];
    } catch (e) {
        return [];
    }
};

export const getHotelAds = async (hotelId: string): Promise<Ad[]> => {
    try {
        const res = await fetch(`/api/ads/public/hotel/${hotelId}`);
        const data = await res.json();
        return data.success ? data.ads : [];
    } catch (e) {
        return [];
    }
};

export const getSearchCTAs = async (): Promise<SearchResultCTA[]> => {
    // Forward-compatible with unified Ad system
    return await getSearchResultsAds() as SearchResultCTA[];
};

// --- Hotel CMS Services (Hybrid: API-First, MongoDB-backed) ---

export const getSavedHotels = async (): Promise<Hotel[]> => {
    try {
        const res = await fetch('/api/admin/hotels');
        const data = await res.json();
        if (data.success && data.hotels) {
            return data.hotels;
        }
        return [];
    } catch (e) {
        console.error('[HYBRID] Failed to fetch hotels from API:', e);
        return [];
    }
};

export const saveHotelToCMS = async (hotel: Hotel): Promise<void> => {
    try {
        const cmsReadyHotel: Record<string, any> = {
            id: hotel.id,
            tourvisioId: (hotel as any).productId || hotel.id,
            productId: hotel.productId,
            name: hotel.name,
            location: hotel.location,
            rating: hotel.rating,
            imageUrl: hotel.imageUrl,
            thumbnailUrl: hotel.thumbnailUrl,
            description: hotel.description,
            shortDescription: hotel.shortDescription,
            images: hotel.images?.map(img => typeof img === 'string' ? { url: img, category: 'other' } : img),
            isEdited: hotel.isEdited
        };

        // Try PUT (update existing)
        const putRes = await fetch(`/api/admin/hotels/${hotel.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(cmsReadyHotel)
        });
        const putData = await putRes.json();
        
        if (!putData.success) {
            console.warn('[HYBRID] PUT failed, attempting POST (create new):', putData.message);
            
            // Fallback to POST (create new)
            const postRes = await fetch('/api/admin/hotels', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(cmsReadyHotel)
            });
            const postData = await postRes.json();
            
            if (postData.success) {
                console.log('[HYBRID] Successfully created new hotel via POST');
            } else {
                console.error('[HYBRID] Both PUT and POST failed:', postData.message);
            }
        } else {
            console.log('[HYBRID] Successfully updated hotel via PUT');
        }
        triggerUpdateEvent();
    } catch (e) {
        console.error('[HYBRID] Failed to save hotel to CMS:', e);
    }
};

export const bulkSaveHotels = async (newHotels: Hotel[]): Promise<void> => {
    try {
        // Save each hotel via API (admin PUT)
        const results = await Promise.allSettled(
            newHotels.map(hotel =>
                fetch(`/api/admin/hotels/${hotel.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ...hotel, isEdited: true })
                })
            )
        );
        const failed = results.filter(r => r.status === 'rejected').length;
        if (failed > 0) console.warn(`[HYBRID] ${failed}/${newHotels.length} hotels failed to save`);
        triggerUpdateEvent();
    } catch (e) {
        console.error('[HYBRID] Failed to bulk save hotels:', e);
    }
};

export const getHotelFromCMS = async (id: string): Promise<Hotel | undefined> => {
    try {
        const res = await fetch(`/api/admin/hotels/${id}`);
        const data = await res.json();
        if (data.success && data.hotel) {
            return {
                ...data.hotel,
                id: data.hotel._id || data.hotel.id || data.hotel.hotelId || data.hotel.productId || id
            };
        }
        // Fallback: try hotel-cache API
        const cacheRes = await fetch(`/api/hotel-cache/${id}`);
        const cacheData = await cacheRes.json();
        if (cacheData.success && cacheData.hotel) {
            return {
                ...cacheData.hotel,
                id: cacheData.hotel.id || cacheData.hotel.hotelId || cacheData.hotel.productId || id
            };
        }
        return undefined;
    } catch (e) {
        console.error('[HYBRID] Failed to get hotel from API:', e);
        return undefined;
    }
};

/**
 * Resolve a URL slug to a hotel.
 * Uses the hotelPages API which already has name-based fallback resolution.
 */
export const getHotelBySlug = async (slug: string, hotelId?: string): Promise<Hotel | undefined> => {
    try {
        // hotelPages route already resolves: exact slug → hotelId match → name-based regex 
        const url = hotelId ? `/api/hotels/page/${encodeURIComponent(slug)}?hotelId=${encodeURIComponent(hotelId)}` : `/api/hotels/page/${encodeURIComponent(slug)}`;
        const res = await fetch(url);
        const data = await res.json();
        if (data.success && data.hotel) {
            // Map the hotelPage response into a Hotel-compatible object
            return {
                id: data.hotel.id,
                productId: data.hotel.id,
                name: data.hotel.name,
                rating: data.hotel.rating || 0,
                stars: data.hotel.stars || 0,
                imageUrl: data.hotel.heroImage || '',
                images: (data.hotel.images || []).map((img: any) => typeof img === 'string' ? img : img.url || ''),
                description: data.hotel.description || '',
                location: data.hotel.location || {},
            } as Hotel;
        }
        return undefined;
    } catch (e) {
        console.error('[HYBRID] Failed to resolve hotel by slug:', e);
        return undefined;
    }
};


// Helper to fuzzy match if exact ID not found (useful for manual imports not yet linked to API ID)
export const findHotelContent = async (id: string, name: string): Promise<Hotel | undefined> => {
    // 1. Exact ID Match via API
    const exact = await getHotelFromCMS(id);
    if (exact) return exact;

    // 2. Fuzzy Name Match — search all hotels
    if (name) {
        try {
            const allHotels = await getSavedHotels();
            const normName = name.toLowerCase().replace(/[^a-z0-9]/g, '');
            return allHotels.find(h => {
                const hName = h.name.toLowerCase().replace(/[^a-z0-9]/g, '');
                return hName.includes(normName) || normName.includes(hName);
            });
        } catch (e) {
            return undefined;
        }
    }
    return undefined;
};

export const updateHotel = async (id: string, updates: Partial<Hotel>): Promise<void> => {
    try {
        const res = await fetch(`/api/admin/hotels/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...updates, isEdited: true })
        });
        const data = await res.json();
        if (data.success) {
            triggerUpdateEvent();
        }
    } catch (e) {
        console.error('[HYBRID] Failed to update hotel content:', e);
    }
};

// --- Widget CMS Services (Hybrid: API-backed) ---

const DEFAULT_WIDGET_SETTINGS: WidgetSettings = { campaignActive: false, ctaHotelId: '101083', restrictToWhitelist: false };

export const getWidgetSettings = async (): Promise<WidgetSettings> => {
    try {
        const res = await fetch(`${ADMIN_CONFIG_API}/widget_settings`);
        const data = await res.json();
        let settings = data.success && data.value ? data.value : DEFAULT_WIDGET_SETTINGS;

        // Auto-detect Flash Sale from Unified Ads if not explicitly active in settings
        if (!settings.flashSaleActive) {
            const ads = await getHomepageAds();
            const flashSaleAd = ads.find(a => a.type === 'banner' || a.name?.toLowerCase().includes('flash'));
            if (flashSaleAd) {
                // Bridge unified Ad to legacy WidgetSettings for components not yet fully migrated
                settings = {
                    ...settings,
                    flashSaleActive: true,
                    flashSaleImageUrl: flashSaleAd.mediaUrl,
                    flashSaleTitle: {
                        en: flashSaleAd.headline || flashSaleAd.name,
                        sv: flashSaleAd.headlineSv || flashSaleAd.headline || flashSaleAd.name
                    },
                    flashSaleDescription: {
                        en: flashSaleAd.description || '',
                        sv: flashSaleAd.descriptionSv || flashSaleAd.description || ''
                    },
                    flashSaleLinkUrl: flashSaleAd.trackedUrl || flashSaleAd.destinationUrl,
                    flashSaleBgColor: '#1e3a8a',
                    flashSaleTextColor: '#ffffff'
                };
            }
        }

        return settings;
    } catch (e) {
        return DEFAULT_WIDGET_SETTINGS;
    }
};

export const saveWidgetSettings = async (settings: WidgetSettings): Promise<void> => {
    try {
        await fetch(`${ADMIN_CONFIG_API}/widget_settings`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ value: settings })
        });
        triggerUpdateEvent();
    } catch (e) {
        console.error('[HYBRID] Failed to save widget settings:', e);
    }
};

// --- Dashboard Stats & Charts (Hybrid: API-backed) ---

export const getDashboardStats = async (): Promise<{ totalBookings: number; pendingBookings: number; totalRevenue: number; todayRevenue: number }> => {
    try {
        const res = await fetch('/api/bookings/stats');
        const data = await res.json();
        if (data.success && data.stats) {
            return data.stats;
        }
        return { totalBookings: 0, pendingBookings: 0, totalRevenue: 0, todayRevenue: 0 };
    } catch (e) {
        console.error('[HYBRID] Failed to fetch dashboard stats:', e);
        return { totalBookings: 0, pendingBookings: 0, totalRevenue: 0, todayRevenue: 0 };
    }
};

export const getRevenueChartData = async (): Promise<DashboardChartData> => {
    try {
        const res = await fetch('/api/bookings/chart');
        const data = await res.json();
        if (data.success && data.chart) {
            return data.chart;
        }
        return { labels: [], datasets: [] };
    } catch (e) {
        console.error('[HYBRID] Failed to fetch chart data:', e);
        return { labels: [], datasets: [] };
    }
};

// Initialize CMS Data
initializeCMS();
